import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { apiFetchJSON } from '../apiFetch';
import { useConfirm } from '../components/shared/ConfirmDialog';
import {
  countNoun,
  physicalSectionHint,
  sectionDiagnosisActionLabel,
  sectionDiagnosisLabel,
} from './semanticReviewPresentation';
import './SemanticReviewScreen.css';

const STATUS_LABELS = {
  WAITING_CLIENT_SEMANTIC_REVIEW: 'Aguardando revisão',
  CLIENT_SEMANTIC_APPROVED: 'Revisão aprovada',
  CLIENT_SEMANTIC_PARTIAL_APPROVED: 'Revisão parcialmente aprovada',
  CLIENT_SEMANTIC_REJECTED: 'Rejeitado',
  CLIENT_APPROVED_SECTION: 'Aprovada na revisão',
  CLIENT_REJECTED_SECTION: 'Rejeitada',
  CLIENT_LEFT_FOR_FALLBACK: 'Mantida fora do conjunto aceito',
  CLIENT_REQUESTED_REGENERATION: 'Regeneração solicitada',
  CLIENT_REQUESTED_PLANT_CORRECTION: 'Alteração de planta solicitada',
  WAITING_ACTIVATION_APPROVAL: 'Histórico legado: staging pendente',
  WAITING_RUNTIME_EXECUTION: 'Histórico legado: execução pendente',
  CLIENT_ACTIVATION_APPROVED: 'Histórico legado: ativação aprovada',
  CLIENT_ACTIVATION_CLIENT_REVIEW_REQUIRED: 'Revisão humana obrigatória',
  AUTONOMOUS_REVIEW_COMPLETED: 'Auditor aprovado',
  AUTONOMOUS_REVIEW_BLOCKED: 'Bloqueado no auditor',
  CLIENT_REVIEW_OPENED: 'Revisão aberta',
  CLIENT_REVIEW_COMPLETED: 'Revisão concluída',
  ACTIVATION_STAGING_PREPARED: 'Histórico legado: staging preparado',
  RUNTIME_PROMOTED: 'Histórico legado: candidate promovido',
  RUNTIME_ASSIGNMENT_ACTIVE: 'Histórico legado: assignment registrado',
  QUEUED: 'Na fila',
  LEASED: 'Reservado pelo worker',
  RUNNING: 'Processando',
  FAILED_RETRYABLE: 'Falha temporária',
  COMPLETED_DIAGNOSTIC_ONLY: 'Diagnóstico concluído',
  OFFLINE_RESEARCH_RECORDED: 'Pesquisa offline registrada',
  COMPLETED_CANDIDATE_PREPARED: 'Candidate preparado',
  FAILED_FINAL: 'Falha final',
  BLOCKED_MISSING_INPUT: 'Entrada ausente',
  BLOCKED_POLICY: 'Bloqueado por política',
  PIPELINE_SUPERSEDED_BY_PLANT: 'Substituído por planta',
  OPEN_CLIENT_REVIEW: 'Abrir revisão',
  SUBMIT_CLIENT_REVIEW: 'Salvar revisão',
  REQUEST_ACTIVATION_APPROVAL: 'Ativação legada desativada',
  PREPARE_ACTIVATION_STAGING: 'Ativação legada desativada',
  APPLY_RUNTIME: 'Ativação legada desativada',
  WAIT_RUNTIME_PUBLICATION: 'Ativação legada desativada',
  MONITOR_RUNTIME: 'Ativação legada desativada',
  REVIEW_RECORDED_OFFLINE: 'Revisão registrada',
  OFFLINE_REVIEW_RECORDED: 'Revisão registrada',
  POST_M1_LEGACY_ACTIVATION_DISABLED: 'Ativação legada desativada',
  RESOLVE_BLOCKER: 'Resolver bloqueio',
  WAIT_CURRENT_PLANT_PROCESSING: 'Aguardar planta atual',
  WAIT_AUTONOMOUS_REVIEW: 'Aguardar auditor',
  WAIT_CLIENT_REVIEW_DECISION: 'Aguardar decisão',
  WAIT_RUNTIME_READINESS: 'Aguardar readiness',
  RUNTIME_DIAGNOSTIC_UNAVAILABLE: 'Diagnóstico indisponível',
  TRACE_NOT_FOUND: 'Sem diagnóstico',
  LOCUTOR_ENDED_CONFIRMED: 'Confirmado pelo player',
  PLAY_LOCUTOR_DELIVERED_TO_EXE: 'Entregue ao player',
  PLAY_LOCUTOR_NOT_DELIVERED: 'Não entregue',
  AUDIO_READY_NOT_SENT_YET: 'Áudio pronto',
  TTS_IN_PROGRESS_OR_NOT_RECORDED: 'TTS em andamento',
  GENERATION_IN_PROGRESS_OR_NOT_RECORDED: 'Geração em andamento',
  TRIGGER_RECEIVED: 'Disparo recebido',
  TRACE_STARTED: 'Diagnóstico iniciado',
  CLIENT_REVIEW_NOT_FOUND: 'Revisão não aberta',
  CLIENT_REVIEW_BLOCKED_TECHNICAL: 'Pré-condição pendente',
  FAIL_AUTONOMOUS_RUNTIME_SPEECH_REVIEW: 'Bloqueado no auditor',
  CAPABILITY_ONLY_AUTONOMOUS_RUNTIME_SPEECH_REVIEW: 'Capacidade estrutural sem fala',
  RUNTIME_SPEECH_ARTIFACTS_MISSING: 'Falas não geradas',
  RUNTIME_SPEECH_PACK_EMPTY: 'Pack vazio',
  RUNTIME_SPEECH_AUDIT_EMPTY: 'Auditoria vazia',
  RUNTIME_SPEECH_PACK_STATUS_MISSING: 'Pack não confirmado',
  BLOCKED_AI_PROVIDER: 'IA indisponível',
  AI_PROVIDER_CREDITS_DEPLETED: 'Créditos IA esgotados',
  AI_PROVIDER_RATE_LIMITED: 'Cota IA atingida',
  AI_PROVIDER_NOT_CONFIGURED: 'IA não configurada',
  AI_PROVIDER_TIMEOUT: 'Timeout da IA',
  AI_PROVIDER_UNAVAILABLE: 'IA indisponível',
  WAITING_HUMAN_REVIEW: 'Revisão técnica',
  AUTONOMOUS_REVIEW_FAILED: 'Bloqueado no auditor',
  AUTONOMOUS_REVIEW_REQUIRES_HUMAN_REVIEW: 'Revisão técnica',
  CANDIDATE_PLANT_HASH_STALE: 'Planta alterada',
  CLIENT_REVIEW_EXPIRED_PLANT_CHANGED: 'Revisão expirada',
  SUPERSEDED_BY_NEW_PLANT_HASH: 'Substituído por nova planta',
  APPROVED_AUTONOMOUS_RUNTIME: 'Aprovada pelo auditor',
  CORE_BLOCKED_DATA: 'Dados autoritativos insuficientes',
  CORE_BLOCKED_HARD: 'Bloqueio técnico',
  PROVIDER_ENRICHMENT_REJECTED: 'Enriquecimento editorial rejeitado',
  SYSTEM_EXCLUDED_SECTION: 'Fora do conjunto aceito',
  SYSTEM_REQUIRES_HUMAN_REVIEW: 'Revisão técnica',
  EXCLUDED_RUNTIME_PACK_BLOCKED: 'Bloqueada no pack',
  EXCLUDED_INSUFFICIENT_VARIANTS: 'Sem falas publicáveis',
  EXCLUDED_TEMPLATE_STRUCTURE: 'Fala repetitiva',
  EXCLUDED_PROSODY_FRAGMENTED: 'Fala fracionada',
  EXCLUDED_CONSTRUCTION_WEAK: 'Construção fraca',
  SYSTEM_REVIEWABLE_ADVISORY_SECTION: 'Rascunho para edição',
  SYSTEM_CAPABILITY_LIMITED_SECTION: 'Capacidade estrutural',
  STRUCTURAL_ONLY_AVAILABLE: 'Capacidade estrutural disponível',
  HUMAN_EDIT_REQUIRED: 'Precisa ser editado',
  REJECTED_PROSODY_FRAGMENTED: 'Fala fracionada',
  REJECTED_CONSTRUCTION_WEAK: 'Construção fraca',
  REQUIRES_HUMAN_REVIEW: 'Revisão técnica',
  NO_SEMANTIC_CANDIDATE: 'Sem candidate',
  RUNTIME_ACTIVE: 'Candidate atribuído (offline)',
  RUNTIME_DISABLED: 'Histórico legado: runtime desligado',
  RUNTIME_LOG_ONLY: 'Histórico legado: modo diagnóstico',
  RUNTIME_CANDIDATE_MISMATCH: 'Outro candidate atribuído',
  RUNTIME_CANONICAL_BLOCKED: 'Histórico legado: canonical bloqueado',
  RUNTIME_TRAFFIC_ZERO: 'Histórico legado: tráfego zero',
  RUNTIME_WAITING_REVIEW: 'Aguardando revisão',
  RUNTIME_WAITING_EXECUTION: 'Histórico legado: execução pendente',
  OFFLINE_CANDIDATE_ASSIGNED: 'Candidate atribuído (offline)',
  OFFLINE_PUBLICATION_RECORDED: 'Histórico legado: publicação registrada',
  OFFLINE_PUBLICATION_RUNNING: 'Histórico legado: request em execução no corte',
  OFFLINE_PUBLICATION_QUEUED: 'Histórico legado: request pendente no corte',
  OFFLINE_PUBLICATION_FAILED: 'Histórico legado: publicação falhou',
  ACTIVE: 'Histórico legado: assignment registrado',
  OFFLINE_REVIEW_APPROVED: 'Revisão registrada',
  OFFLINE_CANDIDATE_AVAILABLE: 'Candidate offline disponível',
  NO_OFFLINE_CANDIDATE: 'Sem candidate offline',
  M1_FACTUAL_PLAYBACK_CONFIRMED: 'Execução confirmada pelo player',
  M1_FACTUAL_DELIVERED_TO_PLAYER: 'Entregue ao player',
  M1_FACTUAL_COMPOSED_NOT_DELIVERED: 'Composta; entrega não confirmada',
  NO_DETERMINISTIC_EXECUTION_OBSERVED: 'Nenhuma execução factual observada',
  DETERMINISTIC_EXECUTION_DIAGNOSTIC_UNAVAILABLE: 'Diagnóstico factual indisponível',
  LEGACY_DIAGNOSTIC_ONLY: 'Somente diagnóstico legado',
  CANDIDATE_BLOCKED: 'Não foi possível concluir',
  SCHEDULER_BLOCKED: 'Aguardando próximo ciclo',
};

const REPROCESS_TRIGGER_LABELS = {
  CLIENT_REQUESTED_FULL_PLANT_REPROCESS: 'Regeneração completa solicitada',
  CLIENT_REQUESTED_RUNTIME_SPEECH_SECTION_REGENERATION: 'Regeneração de seção',
  PLANT_UPDATED: 'Planta alterada',
  RECONCILED_STALE_PLANT: 'Reconciliado por planta pendente',
};

const REPROCESS_SCOPE_LABELS = {
  FULL: 'Planta inteira',
  SECTION: 'Seção',
};

const DESIRED_OUTCOME_LABELS = {
  CLIENT_SEMANTIC_REVIEW: 'Revisão semântica',
  OFFLINE_RESEARCH: 'Pesquisa offline',
};

const CLIENT_REVIEW_WAITING_STATUS = 'WAITING_CLIENT_SEMANTIC_REVIEW';
const LEGACY_DIAGNOSTIC_ONLY = 'LEGACY_DIAGNOSTIC_ONLY';
const SCREEN_LIFECYCLE = LEGACY_DIAGNOSTIC_ONLY;
const TECHNICALLY_APPROVED_SECTION = 'SYSTEM_APPROVED_TECHNICAL_SECTION';
const REVIEWABLE_ADVISORY_SECTION = 'SYSTEM_REVIEWABLE_ADVISORY_SECTION';
const HUMAN_EDIT_REQUIRED = 'HUMAN_EDIT_REQUIRED';
const SEMANTIC_APPROVAL_STATUSES = new Set([
  'CLIENT_SEMANTIC_APPROVED',
  'CLIENT_SEMANTIC_PARTIAL_APPROVED',
]);
const CLIENT_LEFT_FOR_FALLBACK_DECISION = 'CLIENT_LEFT_FOR_FALLBACK';
const CLIENT_APPROVED_SECTION_DECISION = 'CLIENT_APPROVED_SECTION';
const CLIENT_ACCEPTED_SPEECH_DECISION = 'CLIENT_ACCEPTED_SPEECH';
const CLIENT_REJECTED_SPEECH_DECISION = 'CLIENT_REJECTED_SPEECH';
const CLIENT_ACCEPTED_ALL_PRESENTED_SPEECHES = 'CLIENT_ACCEPTED_ALL_PRESENTED_SPEECHES';
const EXPLICIT_PER_SPEECH_DECISION_MODE = 'EXPLICIT_PER_SPEECH';
const STRUCTURAL_ONLY_RETAIL_TEMPLATE_POOL = 'STRUCTURAL_ONLY_RETAIL_TEMPLATE_POOL';
const STRUCTURAL_RETAIL_CONTEXT_PREFIX = 'structural-retail-context-';
const MIN_ACCEPTED_SPEECHES_PER_SECTION = 3;
const SHA256_RE = /^[a-f0-9]{64}$/;
const SPEECH_EDIT_MAX_CHARS = 260;
const REVIEW_STATUS_POLL_INTERVAL_MS = 3000;

const ACTIVE_PROCESSING_JOB_STATUSES = new Set([
  'QUEUED',
  'LEASED',
  'RUNNING',
  'FAILED_RETRYABLE',
]);

function semanticReviewInteractionMode(source = null) {
  if (!source || typeof source !== 'object') return null;
  const values = [
    source.interactionMode,
    source.clientReview?.interactionMode,
    source.review?.interactionMode,
    source.stateProjection?.interactionMode,
    source.stateProjection?.ui?.interactionMode,
  ];
  return values
    .map(value => String(value || '').trim())
    .find(Boolean) || null;
}

function semanticReviewIsLegacyDiagnosticOnly(...sources) {
  return sources.some(source => semanticReviewInteractionMode(source) === LEGACY_DIAGNOSTIC_ONLY);
}

function semanticReviewProcessingJobInProgress(job) {
  if (!job?.exists) return false;
  return job.processing === true
    || ACTIVE_PROCESSING_JOB_STATUSES.has(job.jobStatus)
    || ACTIVE_PROCESSING_JOB_STATUSES.has(job.pipelineStatus)
    || ACTIVE_PROCESSING_JOB_STATUSES.has(job.status);
}

function semanticReviewProcessingJobTerminal(job) {
  if (!job?.exists || semanticReviewProcessingJobInProgress(job)) return false;
  if (typeof job.terminal === 'boolean') return job.terminal;
  return job.failed === true || job.completed === true;
}

function semanticReviewProcessingJobReviewReady(job) {
  if (!job?.exists) return false;
  if (typeof job.reviewReady === 'boolean') return job.reviewReady;
  // Sem a projecao server-side baseada no artefato de review atual nao existe
  // prova suficiente para exibir sucesso. Candidate/status do job, sozinhos,
  // sao apenas sinais de roteamento e falham fechado.
  return false;
}

function semanticReviewProcessingJobOutcomeReached(job) {
  if (!job?.exists) return false;
  if (typeof job.outcomeReached === 'boolean') return job.outcomeReached;

  const statuses = [job.status, job.jobStatus, job.pipelineStatus].filter(Boolean);
  const expectsClientReview = job.desiredOutcome === 'CLIENT_SEMANTIC_REVIEW'
    || statuses.includes('WAITING_HUMAN_REVIEW')
    || statuses.includes(CLIENT_REVIEW_WAITING_STATUS);
  if (expectsClientReview) return semanticReviewProcessingJobReviewReady(job);
  return semanticReviewProcessingJobTerminal(job) && job.failed !== true && job.completed === true;
}

function semanticReviewProcessingJobOfflineResearchRecorded(job) {
  if (!job?.exists || job.failed === true) return false;
  if (semanticReviewProcessingJobReviewReady(job)) return false;
  const statuses = [job.status, job.jobStatus, job.pipelineStatus].filter(Boolean);
  if (statuses.includes('OFFLINE_RESEARCH_RECORDED')) return true;
  return job.desiredOutcome === 'OFFLINE_RESEARCH'
    && semanticReviewProcessingJobTerminal(job)
    && semanticReviewProcessingJobOutcomeReached(job);
}

function semanticReviewProcessingJobTerminalWithPendingOutcome(job) {
  if (!job?.exists) return false;
  if (typeof job.terminalWithPendingOutcome === 'boolean') return job.terminalWithPendingOutcome;
  return semanticReviewProcessingJobTerminal(job)
    && job.failed !== true
    && !semanticReviewProcessingJobOutcomeReached(job);
}

function semanticReviewCandidateIsHistorical(processingJob, candidate) {
  if (!processingJob?.exists || !candidate?.exists) return false;
  if (processingJob.displayedCandidateHistorical === true) return true;
  if (processingJob.displayedCandidateMatchesJob === true) return false;
  const sameJob = Boolean(
    processingJob.jobId
    && candidate.jobId
    && processingJob.jobId === candidate.jobId
  );
  return !sameJob;
}

function semanticReviewStatusNeedsPolling(status) {
  if (!status) return true;
  return semanticReviewProcessingJobInProgress(status.processingJob);
}

function processingJobTrackingKey(job) {
  if (!job?.exists) return '';
  return job.jobId || job.targetPlantHash || job.status || job.jobStatus || '';
}

function processingJobTerminalMessage(job) {
  if (!job?.exists || semanticReviewProcessingJobInProgress(job)) return '';
  if (job.failed === true) {
    return job.lastErrorCode
      ? `Reprocessamento terminou com falha: ${labelForReason(job.lastErrorCode)}.`
      : 'Reprocessamento terminou com falha. Atualize o mapa ou tente novamente.';
  }
  if (semanticReviewProcessingJobOfflineResearchRecorded(job)) {
    return 'Pesquisa offline registrada. Esta rodada não abriu revisão do cliente.';
  }
  if (semanticReviewProcessingJobOutcomeReached(job)) {
    if (semanticReviewProcessingJobReviewReady(job)) {
      return 'Reprocessamento encerrado. A revisão semântica está disponível.';
    }
    return 'Reprocessamento encerrado. O objetivo operacional desta rodada foi atingido.';
  }
  if (semanticReviewProcessingJobTerminalWithPendingOutcome(job)) {
    if (job.candidatePrepared === true || job.hasCandidate === true) {
      return 'Reprocessamento encerrado com pendência. A estrutura do candidate foi criada, mas a revisão do cliente não foi aberta.';
    }
    return 'Reprocessamento encerrado com pendência. Nenhuma revisão do cliente foi aberta para esta rodada.';
  }
  if (semanticReviewProcessingJobTerminal(job)) {
    return 'Reprocessamento encerrado. Confira o resultado operacional desta rodada.';
  }
  return 'Reprocessamento atualizado. Confira o estado mais recente da revisão.';
}

const CLIENT_DECISIONS = [
  {
    value: CLIENT_APPROVED_SECTION_DECISION,
    label: 'Aprovar',
    tone: 'success',
    note: 'Aprovado pela tela de revisão semântica.',
  },
  {
    value: 'CLIENT_REJECTED_SECTION',
    label: 'Rejeitar seção',
    tone: 'error',
    note: 'Seção rejeitada pela tela de revisão semântica.',
  },
  {
    value: CLIENT_LEFT_FOR_FALLBACK_DECISION,
    label: 'Não aceitar',
    tone: 'warning',
    note: 'Mantido fora do conjunto aceito pela tela de revisão semântica.',
  },
  {
    value: 'CLIENT_REQUESTED_REGENERATION',
    label: 'Regenerar',
    tone: 'warning',
    note: 'Solicitada regeneração pela tela de revisão semântica.',
  },
  {
    value: 'CLIENT_REQUESTED_PLANT_CORRECTION',
    label: 'Alterar planta',
    tone: 'warning',
    note: 'Alteração da planta solicitada explicitamente pelo usuário.',
  },
];

const CLIENT_SECTION_RESOLUTION_DECISIONS = CLIENT_DECISIONS.filter(
  decision => decision.value !== CLIENT_APPROVED_SECTION_DECISION
);

const SECTION_DIAGNOSIS_EVALUATION_LABELS = {
  READY: 'Diagnóstico disponível',
  EVALUATION_UNAVAILABLE: 'Avaliação global indisponível',
  IDENTITY_CONFLICT: 'Conflito de identidade física',
  ARTIFACT_CHAIN_UNAVAILABLE: 'Cadeia de artefatos indisponível',
  ARTIFACT_CHAIN_STALE: 'Cadeia de artefatos desatualizada',
  SEMANTIC_SECTION_DIAGNOSIS_IDENTITY_CONFLICT: 'Conflito de identidade física',
  SEMANTIC_SECTION_DIAGNOSIS_ARTIFACT_CHAIN_UNAVAILABLE: 'Cadeia de artefatos indisponível',
  SEMANTIC_SECTION_DIAGNOSIS_ARTIFACT_CHAIN_STALE: 'Cadeia de artefatos desatualizada',
};

function labelForStatus(status) {
  return STATUS_LABELS[status] || status || 'Indisponível';
}

function toneForStatus(status) {
  if (!status) return 'muted';
  if (status.includes('AI_PROVIDER') || status === 'BLOCKED_AI_PROVIDER') return 'warning';
  if (status === REVIEWABLE_ADVISORY_SECTION || status === HUMAN_EDIT_REQUIRED) return 'warning';
  if (status.includes('APPROVED')) return 'success';
  if (status.includes('WAITING')) return 'warning';
  if (
    status.includes('FAIL') ||
    status.includes('REJECTED') ||
    status.includes('BLOCKED') ||
    status.includes('ERROR') ||
    status.includes('EXPIRED') ||
    status.includes('STALE') ||
    status.includes('SUPERSEDED')
  ) return 'error';
  if (status.includes('EXCLUDED')) return 'warning';
  return 'muted';
}

function toneForSectionDiagnosisSeverity(severity) {
  if (severity === 'HIGH') return 'error';
  if (severity === 'MEDIUM' || severity === 'LOW') return 'warning';
  return 'success';
}

function toneForSectionDiagnosisTotals(totals = {}) {
  if (Number(totals.high || 0) > 0) return 'error';
  if (Number(totals.medium || 0) > 0 || Number(totals.low || 0) > 0) return 'warning';
  if (Number(totals.sections || 0) > 0) return 'success';
  return 'muted';
}

function sectionDiagnosisEvaluationLabel(status) {
  return SECTION_DIAGNOSIS_EVALUATION_LABELS[status] || status || 'Diagnóstico global indisponível';
}

function sectionDiagnosisRows(diagnosis) {
  const rows = Array.isArray(diagnosis?.sections) ? diagnosis.sections : [];
  const rank = { HIGH: 0, MEDIUM: 1, LOW: 2, OK: 3 };
  return [...rows].sort((left, right) => {
    const leftRank = rank[left?.diagnosis?.severity] ?? 4;
    const rightRank = rank[right?.diagnosis?.severity] ?? 4;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(left?.sectionName || '').localeCompare(String(right?.sectionName || ''));
  });
}

function sectionDiagnosisIsClean(diagnosis) {
  if (diagnosis?.evaluation?.evaluable !== true) return false;
  const totals = diagnosis?.totals || {};
  const rows = sectionDiagnosisRows(diagnosis);
  return rows.length > 0
    && Number(totals.high || 0) === 0
    && Number(totals.medium || 0) === 0
    && Number(totals.low || 0) === 0
    && rows.every(section => section?.diagnosis?.severity === 'OK');
}

function shortId(value) {
  if (!value) return 'Nenhum';
  const text = String(value);
  if (text.length <= 34) return text;
  return `${text.slice(0, 20)}...${text.slice(-10)}`;
}

function candidateDisplayId(value) {
  if (!value) return 'Nenhum';
  const text = String(value);
  const match = text.match(/^(semantic-candidate-\d{8}-\d{6})-/);
  return match ? match[1] : shortId(text);
}

function CandidateIdBlock({ value }) {
  const display = candidateDisplayId(value);
  const full = value ? String(value) : '';

  return (
    <span className="semantic-review-candidate-id">
      <span className="semantic-review-candidate-id__main">{display}</span>
      {full && full !== display && (
        <span className="semantic-review-candidate-id__full">{full}</span>
      )}
    </span>
  );
}

function listCount(values) {
  return Array.isArray(values) ? values.length : 0;
}

function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0%';
  return `${Math.round(numeric * 100)}%`;
}

function formatTimestamp(value) {
  if (!value) return 'Indisponível';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Indisponível';
  return date.toLocaleString('pt-BR');
}

function formatOptionalPercent(value, fallback = 'Sem trava') {
  if (value === null || value === undefined) return fallback;
  return formatPercent(value);
}

function decisionMeta(value) {
  return CLIENT_DECISIONS.find(decision => decision.value === value) || null;
}

function normalizeSectionKeyText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function reviewSectionDecisionKey(section, index = 0) {
  if (section?.sectionKey) return String(section.sectionKey);
  if (section?.sectionId) return `section-id:${section.sectionId}`;
  const normalizedName = normalizeSectionKeyText(section?.sectionName);
  if (normalizedName) return `section-name:${normalizedName}:${index}`;
  return `section-index:${index}`;
}

function shortHash(value) {
  const text = String(value || '');
  return text ? text.slice(0, 10) : '';
}

function approvedSpeechesForSection(section) {
  return Array.isArray(section?.approvedSpeeches)
    ? section.approvedSpeeches.filter(speech => String(speech?.text || '').trim())
    : [];
}

function sectionUsesStructuralRetailTemplatePool(section) {
  if (!section || typeof section !== 'object') return false;
  if (section.generationAuthorityMode === STRUCTURAL_ONLY_RETAIL_TEMPLATE_POOL) return true;

  const speeches = approvedSpeechesForSection(section);
  return speeches.length >= MIN_ACCEPTED_SPEECHES_PER_SECTION
    && speeches.every(speech =>
      String(speech?.generationAuthorityMode || '') === STRUCTURAL_ONLY_RETAIL_TEMPLATE_POOL
      || String(speech?.variantContextId || '').startsWith(STRUCTURAL_RETAIL_CONTEXT_PREFIX)
    );
}

function advisorySpeechesForSection(section) {
  return Array.isArray(section?.advisorySpeeches)
    ? section.advisorySpeeches.filter(speech => String(speech?.text || '').trim())
    : [];
}

function sectionRequiresHumanEdit(section) {
  return section?.systemStatus === REVIEWABLE_ADVISORY_SECTION
    || section?.status === REVIEWABLE_ADVISORY_SECTION
    || section?.reviewDisposition === HUMAN_EDIT_REQUIRED;
}

function displayedSpeechesForSection(section) {
  return sectionRequiresHumanEdit(section)
    ? advisorySpeechesForSection(section)
    : approvedSpeechesForSection(section);
}

function advisorySpeechIdentitiesComplete(section) {
  const sectionKey = String(section?.sectionKey || '').trim();
  const speeches = advisorySpeechesForSection(section);
  return Boolean(sectionKey)
    && speeches.length > 0
    && speeches.every(speech => SHA256_RE.test(String(speech?.textSha256 || '').trim()));
}

function exactSpeechDecisionKey(section, speech) {
  const sectionKey = String(section?.sectionKey || '').trim();
  const speechId = String(speech?.speechId || speech?.id || '').trim();
  const variantContextId = String(speech?.variantContextId || '').trim();
  const textSha256 = String(speech?.textSha256 || '').trim();
  if (!sectionKey || !speechId || !SHA256_RE.test(textSha256)) return null;
  return `${sectionKey}::${speechId}::${variantContextId}::${textSha256}`;
}

function speechDecisionEntry(decisions, section, speech) {
  const key = exactSpeechDecisionKey(section, speech);
  return key ? decisions?.[key] || null : null;
}

function sectionSpeechDecisionState(section, speechDecisions, sectionSpeechDecisionModes) {
  const speeches = approvedSpeechesForSection(section);
  const sectionKey = String(section?.sectionKey || '').trim();
  const presentedSpeechSetSha256 = String(section?.presentedSpeechSetSha256 || '').trim();
  const entries = speeches.map(speech => speechDecisionEntry(speechDecisions, section, speech));
  const acceptedCount = entries.filter(entry => entry?.decision === CLIENT_ACCEPTED_SPEECH_DECISION).length;
  const rejectedCount = entries.filter(entry => entry?.decision === CLIENT_REJECTED_SPEECH_DECISION).length;
  const decidedCount = acceptedCount + rejectedCount;
  const acceptedVariantContextCount = new Set(
    speeches
      .filter((speech, index) => entries[index]?.decision === CLIENT_ACCEPTED_SPEECH_DECISION)
      .map(speech => String(speech?.variantContextId || '').trim())
      .filter(Boolean)
  ).size;
  const variantContextRequired = section?.variantContextRequired === true;
  const declaredMinimum = Number(section?.minimumClientAcceptedSpeechVariants);
  const minimumAcceptedSpeechVariants = Number.isInteger(declaredMinimum) && declaredMinimum >= 1
    ? declaredMinimum
    : MIN_ACCEPTED_SPEECHES_PER_SECTION;
  const styleExampleCount = entries.filter(entry =>
    entry?.decision === CLIENT_ACCEPTED_SPEECH_DECISION && entry?.useAsStyleExample === true
  ).length;
  const identitiesComplete = Boolean(
    sectionKey &&
    SHA256_RE.test(String(section?.sectionEvidenceHash || '').trim()) &&
    SHA256_RE.test(presentedSpeechSetSha256) &&
    speeches.length > 0 &&
    speeches.every(speech => {
      const speechId = String(speech?.speechId || speech?.id || '').trim();
      const textSha256 = String(speech?.textSha256 || '').trim();
      const variantContextId = String(speech?.variantContextId || '').trim();
      return Boolean(speechId)
        && SHA256_RE.test(textSha256)
        && (!variantContextRequired || Boolean(variantContextId));
    })
  );
  const allDecided = speeches.length > 0 && decidedCount === speeches.length;
  const variantContextsReady = !variantContextRequired ||
    acceptedVariantContextCount >= minimumAcceptedSpeechVariants;
  const ready = identitiesComplete &&
    allDecided &&
    acceptedCount >= minimumAcceptedSpeechVariants &&
    variantContextsReady;
  const mode = sectionKey ? sectionSpeechDecisionModes?.[sectionKey] || null : null;

  return {
    speeches,
    sectionKey,
    presentedSpeechSetSha256,
    totalCount: speeches.length,
    acceptedCount,
    rejectedCount,
    decidedCount,
    acceptedVariantContextCount,
    variantContextRequired,
    minimumAcceptedSpeechVariants,
    variantContextsReady,
    pendingCount: Math.max(0, speeches.length - decidedCount),
    styleExampleCount,
    identitiesComplete,
    allDecided,
    ready,
    mode,
  };
}

function resolvedSectionDecision(section, sectionIndex, sectionDecisions, speechDecisions, sectionSpeechDecisionModes) {
  const explicitResolution = sectionDecisions?.[reviewSectionDecisionKey(section, sectionIndex)] || null;
  if (explicitResolution) return explicitResolution;
  const speechState = sectionSpeechDecisionState(section, speechDecisions, sectionSpeechDecisionModes);
  return speechState.ready ? CLIENT_APPROVED_SECTION_DECISION : null;
}

function removeSectionSpeechDecisions(decisions, sectionKey) {
  const prefix = `${String(sectionKey || '').trim()}::`;
  if (prefix === '::') return decisions;
  return Object.fromEntries(
    Object.entries(decisions || {}).filter(([key]) => !key.startsWith(prefix))
  );
}

function orientationList(values, limit = 6) {
  return Array.isArray(values)
    ? values.map(value => String(value || '').trim()).filter(Boolean).slice(0, limit)
    : [];
}

function buildDetailedSectionMap(reviewDetail) {
  const sections = Array.isArray(reviewDetail?.sectionDecisions) ? reviewDetail.sectionDecisions : [];
  return new Map(sections.map((section, index) => [reviewSectionDecisionKey(section, index), section]));
}

function speechEditKey(section, sectionIndex, speech, speechIndex) {
  const sectionKey = reviewSectionDecisionKey(section, sectionIndex);
  return `${sectionKey}::${speech?.textSha256 || speech?.id || speechIndex}`;
}

function speechEditSectionKeys(section, sectionIndex) {
  const hasExactIdentity = Boolean(section?.sectionKey || section?.sectionId);
  const keys = [
    reviewSectionDecisionKey(section, sectionIndex),
    section?.sectionKey,
    section?.sectionId ? `section-id:${section.sectionId}` : null,
  ];
  const normalizedName = normalizeSectionKeyText(section?.sectionName);
  if (!hasExactIdentity && normalizedName) {
    keys.push(`section-name:${normalizedName}`);
    keys.push(`section-name:${normalizedName}:${sectionIndex}`);
  }
  if (!hasExactIdentity) keys.push(`section-index:${sectionIndex}`);
  return [...new Set(keys.map(key => String(key || '').trim()).filter(Boolean))];
}

function speechEditKeysForSpeech(section, sectionIndex, speech, speechIndex) {
  const speechKey = speech?.textSha256 || speech?.id || speechIndex;
  return speechEditSectionKeys(section, sectionIndex).map(sectionKey => `${sectionKey}::${speechKey}`);
}

function speechEditProjectionKeys(edit) {
  const hasExactIdentity = Boolean(edit?.sectionKey || edit?.sectionId);
  const sectionKeys = [
    edit?.sectionKey,
    edit?.sectionId ? `section-id:${edit.sectionId}` : null,
  ];
  const normalizedName = normalizeSectionKeyText(edit?.sectionName);
  if (!hasExactIdentity && normalizedName) sectionKeys.push(`section-name:${normalizedName}`);
  const speechKey = edit?.targetSpeechTextSha256 || edit?.originalTextSha256 || null;
  if (!speechKey) return [];
  return [...new Set(sectionKeys.map(key => String(key || '').trim()).filter(Boolean))]
    .map(sectionKey => `${sectionKey}::${speechKey}`);
}

function speechEditStatusLabel(status) {
  switch (status) {
    case 'SUBMITTED_FOR_REAUDIT':
      return 'Aguardando reauditoria';
    case 'APPROVED_BY_REAUDIT':
      return 'Aprovada na reauditoria';
    case 'REJECTED_BY_REAUDIT':
      return 'Rejeitada na reauditoria';
    case 'SUPERSEDED_BY_NEW_EDIT':
      return 'Substituída por nova sugestão';
    default:
      return status || 'Sugestão registrada';
  }
}

function speechEditStatusTone(status) {
  if (status === 'APPROVED_BY_REAUDIT') return 'success';
  if (status === 'REJECTED_BY_REAUDIT' || status === 'SUPERSEDED_BY_NEW_EDIT') return 'error';
  if (status === 'SUBMITTED_FOR_REAUDIT') return 'warning';
  return 'muted';
}

function newestSpeechEdit(current, candidate) {
  if (!current) return candidate;
  const currentTime = Date.parse(current.submittedAt || '') || 0;
  const candidateTime = Date.parse(candidate.submittedAt || '') || 0;
  return candidateTime >= currentTime ? candidate : current;
}

function buildSpeechEditProjectionMap(reviewDetail) {
  const edits = Array.isArray(reviewDetail?.speechEdits?.edits) ? reviewDetail.speechEdits.edits : [];
  const map = new Map();
  for (const edit of edits) {
    for (const key of speechEditProjectionKeys(edit)) {
      map.set(key, newestSpeechEdit(map.get(key), edit));
    }
  }
  return map;
}

function speechEditProjectionForSpeech(projectionMap, section, sectionIndex, speech, speechIndex) {
  if (!projectionMap || typeof projectionMap.get !== 'function') return null;
  for (const key of speechEditKeysForSpeech(section, sectionIndex, speech, speechIndex)) {
    const edit = projectionMap.get(key);
    if (edit) return edit;
  }
  return null;
}

function normalizeSpeechEditText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function isStructuralReviewDraftSpeech(speech = {}) {
  return String(speech?.advisoryOrigin || speech?.origin || '').trim()
    === 'SYSTEM_STRUCTURAL_REVIEW_DRAFT';
}

function speechEditValidationMessage(text, originalText, {
  allowUnchanged = false,
} = {}) {
  const cleanText = normalizeSpeechEditText(text);
  if (!cleanText) return 'Escreva uma sugestão antes de enviar.';
  if (cleanText.length > SPEECH_EDIT_MAX_CHARS) {
    return `A sugestão deve ter até ${SPEECH_EDIT_MAX_CHARS} caracteres.`;
  }
  if (!allowUnchanged
    && normalizeSectionKeyText(cleanText) === normalizeSectionKeyText(originalText)) {
    return 'A sugestão precisa mudar a fala original.';
  }
  return '';
}

const BLOCK_REASON_LABELS = {
  RUNTIME_SPEECH_SECTION_EXCLUDED: 'Seção excluída do pack',
  RUNTIME_SPEECH_VARIANTS_INSUFFICIENT: 'Poucas variações aprovadas',
  SOURCE_VARIANT_CONTEXT_COVERAGE_INSUFFICIENT: 'Cobertura de contextos da fala insuficiente',
  RUNTIME_SPEECH_TOO_GENERIC_FOR_SUBFAMILY: 'Fala genérica demais',
  RUNTIME_SPEECH_CROSS_SECTION_CONTAMINATION: 'Mistura de seções',
  RUNTIME_SPEECH_TEMPLATE_STRUCTURE: 'Estrutura repetitiva',
  RUNTIME_SPEECH_PROSODY_FRAGMENTED: 'Fala fracionada',
  RUNTIME_SPEECH_CONSTRUCTION_WEAK: 'Construção fraca',
  SEMANTIC_EVIDENCE_DIRTY_ORTHOGRAPHY_LEAK: 'Ortografia da fala precisa de correção',
  RUNTIME_SPEECH_DIRECTIVE_CHOICE_CTA: 'CTA de escolha',
  RUNTIME_SPEECH_UNSUPPORTED_QUALITY_CLAIM: 'Claim sem dado real',
  RUNTIME_SPEECH_AUTHORITY_CLAIM_UNPROVEN: 'Afirmação sem autoridade declarada',
  RUNTIME_SPEECH_WEAK_SPATIAL_FILLER: 'Localização fraca',
  RUNTIME_SPEECH_TECHNICAL_LEAK: 'Texto técnico na fala',
  RUNTIME_SPEECH_ARTIFACTS_MISSING: 'Falas ainda não geradas',
  RUNTIME_SPEECH_PACK_EMPTY: 'Pack sem seções',
  RUNTIME_SPEECH_AUDIT_EMPTY: 'Auditoria sem seções',
  RUNTIME_SPEECH_PACK_STATUS_MISSING: 'Pack não confirmado',
  RUNTIME_SPEECH_MISSING: 'Fala semântica ausente',
  RUNTIME_SPEECH_PACK_MISSING: 'Pack de falas ausente',
  RUNTIME_SPEECH_SECTION_MISSING: 'Seção sem fala',
  SEMANTIC_METADATA_MISSING: 'Metadado semântico ausente',
  SECTION_NOT_FOUND: 'Seção não encontrada',
  CANDIDATE_ID_MISSING: 'Candidate não informado',
  CANDIDATE_NOT_FOUND: 'Candidate não encontrado',
  CANDIDATE_STATUS_NOT_FOUND: 'Status do candidate ausente',
  CANDIDATE_STATUS_INVALID: 'Status do candidate inválido',
  RUNTIME_CANONICAL_SOURCE_REQUIRED: 'Fonte canônica indisponível',
  TRAFFIC_BUCKET_OUT_OF_RANGE: 'Fora do percentual de teste',
  URGENCY_CAPPED_WITHOUT_REAL_DATA: 'Urgência reduzida sem dado real',
  SPECIFICITY_CAPPED_WITHOUT_PRODUCT_OR_OFFER_DATA: 'Especificidade reduzida sem produto/oferta',
  COMMERCIAL_INTENSITY_CAPPED_BY_SECTION_SENSITIVITY: 'Intensidade reduzida por seção sensível',
  DIRECTIVITY_CAPPED_WITHOUT_CTA_DATA: 'Diretividade reduzida sem CTA',
  urgency_real_data: 'dado real de urgência',
  specificity_product_offer_data: 'produto ou oferta real',
  directivity_cta_data: 'CTA autorizado',
  SEMANTIC_PACK_CONTROLLED_SWAP: 'Pack semântico',
  GEMINI_LEGACY_OR_PROMPT_CONTEXT: 'Prompt legado',
  SENSITIVE_SECTION_PRE_GATE: 'Pré-gate sensível',
  SCHEDULER_BLOCKED: 'Scheduler bloqueado',
  UNKNOWN: 'Indisponível',
  CLIENT_REVIEW_ARTIFACT_MISSING: 'Artefatos da revisão ausentes',
  PROMOTION_WITHOUT_REAL_DATA: 'Promoção sem dado real',
  SENSITIVE_SECTION_REQUIRES_HUMAN_REVIEW: 'Seção sensível',
  AI_PROVIDER_CREDITS_DEPLETED: 'Créditos IA esgotados',
  AI_PROVIDER_RATE_LIMITED: 'Cota IA atingida',
  AI_PROVIDER_NOT_CONFIGURED: 'IA não configurada',
  AI_PROVIDER_TIMEOUT: 'Timeout da IA',
  AI_PROVIDER_UNAVAILABLE: 'IA indisponível',
  BLOCKED_AI_PROVIDER: 'IA indisponível',
  SECTION_WITHOUT_ELEMENTS: 'Demanda estrutural declarada',
  LOW_ELEMENT_EVIDENCE: 'Contexto disponível é estrutural',
  ELEMENT_EVIDENCE_MISSING: 'Evidência de elemento ausente',
  SECTION_LABEL_REUSED: 'Rótulo usado em mais de uma área',
  SECTION_SEMANTIC_CONTEXT_INVALID: 'Contexto semântico inválido',
  SECTION_SEMANTIC_CONTEXT_NOT_APPROVED: 'Contexto semântico não aprovado',
  SECTION_SEMANTIC_CONTEXT_NOT_FOUND: 'Contexto semântico ausente',
  SECTION_SEMANTIC_CONTEXT_NOT_AI_SOURCED: 'Contexto sem origem autoritativa',
  SECTION_SEMANTIC_CONTEXT_LEVEL2_BRIEF_MISSING: 'Brief semântico incompleto',
  SECTION_SEMANTIC_CONTEXT_AUTHORITY_MODE_INVALID: 'Autoridade semântica inválida',
  SECTION_SEMANTIC_CONTEXT_NEEDS_PROVIDER_ENRICHMENT: 'Enriquecimento semântico pendente',
  SECTION_SEMANTIC_CONTEXT_CORE_INVALID: 'Núcleo semântico inválido',
  SECTION_SEMANTIC_CONTEXT_PROVIDER_ENRICHMENT_INVALID: 'Enriquecimento semântico inválido',
  MACRO_FAMILY_MISSING: 'Família semântica ausente',
  SEMANTIC_SUBFAMILY_MISSING: 'Subfamília semântica ausente',
  ELEMENTS_REQUIRED_FOR_DIFFERENTIATION_MISSING: 'Elementos insuficientes para diferenciar a seção',
  EXCLUDED_RUNTIME_PACK_BLOCKED: 'Seção bloqueada',
  EXCLUDED_INSUFFICIENT_VARIANTS: 'Poucas variações aprovadas',
  EXCLUDED_TEMPLATE_STRUCTURE: 'Estrutura repetitiva',
  EXCLUDED_PROSODY_FRAGMENTED: 'Fala fracionada',
  EXCLUDED_CONSTRUCTION_WEAK: 'Construção fraca',
  REJECTED_PROSODY_FRAGMENTED: 'Fala fracionada',
  REQUIRES_HUMAN_REVIEW: 'Revisão técnica',
};

function labelForReason(reason) {
  return BLOCK_REASON_LABELS[reason] || STATUS_LABELS[reason] || reason || 'Motivo indisponível';
}

const RESPONSIBLE_LABELS = {
  cliente: 'Cliente',
  suporte: 'Suporte',
  sistema: 'Sistema',
  operacao: 'Operação',
};

const AXIS_LABELS = {
  urgencia: 'Urgência',
  especificidade: 'Especificidade',
  intensidadeComercial: 'Intensidade',
  diretividade: 'Diretividade',
};

const CONFIDENCE_LABELS = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

function labelForResponsible(value) {
  return RESPONSIBLE_LABELS[value] || value || 'Indisponível';
}

function labelForAxis(value) {
  return AXIS_LABELS[value] || value || 'Eixo';
}

function formatDiagnosticSignal(value) {
  if (value === null || value === undefined || value === '') return 'Indisponível';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0 && numeric <= 1) return formatPercent(numeric);
  return String(value);
}

function labelForConfidence(value) {
  return CONFIDENCE_LABELS[value] || value || 'Baixa';
}

function labelForTrigger(trigger) {
  return REPROCESS_TRIGGER_LABELS[trigger] || trigger || 'Não informado';
}

function labelForScope(scope) {
  return REPROCESS_SCOPE_LABELS[scope] || scope || 'Planta inteira';
}

function labelForDesiredOutcome(outcome) {
  return DESIRED_OUTCOME_LABELS[outcome] || outcome || 'Diagnóstico';
}

function StatusPill({ status, tone }) {
  const resolvedTone = tone || toneForStatus(String(status || ''));
  return (
    <span className={`semantic-review-pill semantic-review-pill--${resolvedTone}`}>
      {labelForStatus(status)}
    </span>
  );
}

function Metric({ label, value, tone = 'default' }) {
  return (
    <div className={`semantic-review-metric semantic-review-metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CompactMetric({ label, value, tone = 'default' }) {
  return (
    <div className={`semantic-review-compact-metric semantic-review-compact-metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DetailRow({ label, value, align = 'end' }) {
  return (
    <div className={`semantic-review-detail-row semantic-review-detail-row--${align}`}>
      <span>{label}</span>
      <strong title={typeof value === 'string' ? value : undefined}>{value || 'Indisponível'}</strong>
    </div>
  );
}

function SectionList({ title, items, tone = 'default', empty = 'Nenhuma seção' }) {
  const values = Array.isArray(items) ? items : [];
  return (
    <section className="semantic-review-section-list">
      <div className="semantic-review-section-list__header">
        <h3>{title}</h3>
        <span>{values.length}</span>
      </div>
      {values.length > 0 ? (
        <div className="semantic-review-chip-list">
          {values.map(item => (
            <span key={item} className={`semantic-review-chip semantic-review-chip--${tone}`}>
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className="semantic-review-empty">{empty}</p>
      )}
    </section>
  );
}

function SectionOrientationBlock({ orientation, section = null }) {
  if (!orientation) return null;
  if (sectionUsesStructuralRetailTemplatePool(section)) {
    const sectionSubject = section?.sectionName
      ? `a seção ${section.sectionName}`
      : 'esta seção';
    return (
      <div className="semantic-review-orientation">
        <div className="semantic-review-orientation__main">
          <span>Objetivo</span>
          <strong>Informar que {sectionSubject} existe nesta loja.</strong>
        </div>
        <div className="semantic-review-orientation__grid">
          <span>Base</span>
          <strong>Seção confirmada na planta.</strong>
          <span>Limites</span>
          <strong>Sem produtos, preços, promoções, estoque, avaliações ou convites não declarados.</strong>
        </div>
      </div>
    );
  }
  const drift = orientationList(orientation.forbiddenDrift);
  return (
    <div className="semantic-review-orientation">
      <div className="semantic-review-orientation__main">
        <span>Orientação</span>
        <strong>{orientation.promptReadySummary || orientation.useContext || 'Orientação sem resumo disponível.'}</strong>
      </div>
      <div className="semantic-review-orientation__grid">
        <span>Contexto</span>
        <strong>{orientation.useContext || 'Não informado'}</strong>
        <span>Tarefa</span>
        <strong>{orientation.jobToBeDone || 'Não informada'}</strong>
        <span>Confiança</span>
        <strong>{orientation.confidence || 'Não informada'}</strong>
        <span>Fonte</span>
        <strong>{orientation.inferenceSource || 'Não informada'}</strong>
      </div>
      {drift.length > 0 && (
        <div className="semantic-review-orientation__chips">
          {drift.map(item => (
            <span className="semantic-review-chip semantic-review-chip--warning" key={`drift-${item}`}>
              evitar {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionSpeechList({
  section,
  sectionIndex = 0,
  editable = false,
  showDecisionControls = false,
  showTechnicalMeta = false,
  submittingReview = false,
  speechDecisions = {},
  editStates = {},
  persistedSpeechEdits = null,
  onSpeechDecision,
  onToggleStyleExample,
  onOpenSpeechEdit,
  onChangeSpeechEdit,
  onCancelSpeechEdit,
  onSubmitSpeechEdit,
  onMaterializeSpeechEdit,
  materializingSpeechEditId = '',
}) {
  const requiresHumanEdit = sectionRequiresHumanEdit(section);
  const speeches = displayedSpeechesForSection(section);
  const showSpeechDecisionControls = showDecisionControls && !requiresHumanEdit;
  if (speeches.length === 0) {
    return (
      <div className="semantic-review-speech-list semantic-review-speech-list--empty">
        {requiresHumanEdit
          ? 'Nenhum rascunho editável foi carregado para esta seção.'
          : 'Nenhuma fala auditada foi carregada para esta seção.'}
      </div>
    );
  }

  return (
    <div className="semantic-review-speech-list">
      {speeches.map((speech, index) => {
        const editKey = speechEditKey(section, sectionIndex, speech, index);
        const decisionKey = exactSpeechDecisionKey(section, speech);
        const decisionEntry = decisionKey ? speechDecisions[decisionKey] || null : null;
        const accepted = decisionEntry?.decision === CLIENT_ACCEPTED_SPEECH_DECISION;
        const rejected = decisionEntry?.decision === CLIENT_REJECTED_SPEECH_DECISION;
        const editState = editStates[editKey] || {};
        const persistedEdit = speechEditProjectionForSpeech(persistedSpeechEdits, section, sectionIndex, speech, index);
        const editOpen = editState.open === true;
        const submitting = editState.status === 'submitting';
        const submitted = editState.status === 'submitted';
        const hasError = editState.status === 'error';
        const showPersistedEdit = Boolean(persistedEdit && !(submitted && editState.editId === persistedEdit.editId));
        const persistedTone = speechEditStatusTone(persistedEdit?.editStatus);
        const persistedReasons = Array.isArray(persistedEdit?.reaudit?.reasonCodes)
          ? persistedEdit.reaudit.reasonCodes.slice(0, 3)
          : [];
        const persistedEditApproved = persistedEdit?.editStatus === 'APPROVED_BY_REAUDIT';
        const materializingPersistedEdit = persistedEditApproved
          && materializingSpeechEditId === persistedEdit.editId;
        const draftText = editState.text ?? speech.text ?? '';
        const cleanDraft = normalizeSpeechEditText(draftText);
        const structuralReviewDraft = requiresHumanEdit && isStructuralReviewDraftSpeech(speech);
        const speechTextUnchanged = normalizeSectionKeyText(cleanDraft)
          === normalizeSectionKeyText(speech.text);
        const validationMessage = submitted ? '' : speechEditValidationMessage(draftText, speech.text, {
          allowUnchanged: structuralReviewDraft,
        });
        const statusMessage = editState.message || validationMessage || `${cleanDraft.length}/${SPEECH_EDIT_MAX_CHARS}`;
        const statusTone = submitted ? 'success' : (hasError ? 'error' : (validationMessage ? 'warning' : 'muted'));
        const canEditSpeech = editable && Boolean(speech.textSha256);
        const canDecideSpeech = !requiresHumanEdit && editable && Boolean(decisionKey);
        const decisionStatus = accepted ? 'Aceita' : (rejected ? 'Rejeitada' : 'Sem decisão');
        const decisionTone = accepted ? 'success' : (rejected ? 'error' : 'muted');
        return (
          <div
            className={`semantic-review-speech${accepted ? ' is-accepted' : ''}${rejected ? ' is-rejected' : ''}`}
            key={speech.id || speech.textSha256 || index}
          >
            <span>{index + 1}</span>
            <div className="semantic-review-speech__body">
              <p>{speech.text}</p>
              {requiresHumanEdit && (
                <div className="semantic-review-chip-list">
                  {persistedEditApproved ? (
                    <>
                      <span className="semantic-review-chip semantic-review-chip--success">
                        {persistedEdit?.submissionMode === 'EXACT_STRUCTURAL_FORM_SELECTED'
                          ? 'Redação selecionada e aprovada'
                          : 'Edição aprovada'}
                      </span>
                      <span className="semantic-review-chip semantic-review-chip--muted">
                        Rascunho original substituído
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="semantic-review-chip semantic-review-chip--warning">
                        {structuralReviewDraft
                          ? 'Selecione ou edite a redação'
                          : 'Precisa ser editado'}
                      </span>
                      <span className="semantic-review-chip semantic-review-chip--muted">
                        {structuralReviewDraft
                          ? 'Os fatos continuam vindo da planta'
                          : 'Não pode ser aceito como está'}
                      </span>
                    </>
                  )}
                </div>
              )}
              {showSpeechDecisionControls && (
              <div className="semantic-review-speech-decision">
                <div
                  className="semantic-review-speech-decision__actions"
                  role="group"
                  aria-label={`Decisão da fala ${index + 1} em ${section.sectionName}`}
                >
                  <button
                    className={`semantic-review-speech-decision__button semantic-review-speech-decision__button--accept${accepted ? ' is-selected' : ''}`}
                    type="button"
                    aria-pressed={accepted}
                    onClick={() => onSpeechDecision(
                      section,
                      sectionIndex,
                      speech,
                      index,
                      CLIENT_ACCEPTED_SPEECH_DECISION
                    )}
                    disabled={!canDecideSpeech || submittingReview}
                  >
                    Aceitar
                  </button>
                  <button
                    className={`semantic-review-speech-decision__button semantic-review-speech-decision__button--reject${rejected ? ' is-selected' : ''}`}
                    type="button"
                    aria-pressed={rejected}
                    onClick={() => onSpeechDecision(
                      section,
                      sectionIndex,
                      speech,
                      index,
                      CLIENT_REJECTED_SPEECH_DECISION
                    )}
                    disabled={!canDecideSpeech || submittingReview}
                  >
                    Rejeitar
                  </button>
                </div>
                <span
                  className={`semantic-review-speech-decision__status semantic-review-speech-decision__status--${decisionTone}`}
                  aria-live="polite"
                >
                  {decisionStatus}
                </span>
                {accepted && (
                  <label className="semantic-review-style-example">
                    <input
                      type="checkbox"
                      checked={decisionEntry?.useAsStyleExample === true}
                      onChange={event => onToggleStyleExample(
                        section,
                        sectionIndex,
                        speech,
                        index,
                        event.target.checked
                      )}
                      disabled={submittingReview}
                    />
                    <span>Usar como exemplo do meu estilo</span>
                  </label>
                )}
              </div>
              )}
              {showPersistedEdit && (
                <div className={`semantic-review-speech-edit-projection semantic-review-speech-edit-projection--${persistedTone}`}>
                  <div className="semantic-review-speech-edit-projection__header">
                    <strong>{speechEditStatusLabel(persistedEdit.editStatus)}</strong>
                    {showTechnicalMeta && persistedEdit.editedTextSha256 && (
                      <small>{shortHash(persistedEdit.editedTextSha256)}</small>
                    )}
                  </div>
                  <p>{persistedEdit.editedText}</p>
                  {persistedReasons.length > 0 && (
                    <div className="semantic-review-chip-list">
                      {persistedReasons.map(reason => (
                        <span className="semantic-review-chip semantic-review-chip--warning" key={reason}>
                          {labelForReason(reason)}
                        </span>
                      ))}
                    </div>
                  )}
                  {requiresHumanEdit
                    && persistedEditApproved
                    && typeof onMaterializeSpeechEdit === 'function' && (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onMaterializeSpeechEdit(persistedEdit)}
                      disabled={
                        submittingReview
                        || materializingPersistedEdit
                      }
                    >
                      {materializingPersistedEdit
                        ? 'Gerando candidate...'
                        : 'Gerar candidate com esta fala'}
                    </button>
                  )}
                </div>
              )}
              {canEditSpeech && editOpen && (
                <div className="semantic-review-speech-edit">
                  <label className="semantic-review-speech-edit__label">
                    <span>{structuralReviewDraft
                      ? 'Redação estrutural para seleção e reauditoria'
                      : (requiresHumanEdit ? 'Edição obrigatória para reauditoria' : 'Sugestão para reauditoria')}</span>
                    <textarea
                      className="textarea semantic-review-speech-edit__textarea"
                      value={draftText}
                      onChange={event => onChangeSpeechEdit(editKey, event.target.value)}
                      rows={3}
                      maxLength={SPEECH_EDIT_MAX_CHARS}
                      disabled={submitting || submitted}
                    />
                  </label>
                  {structuralReviewDraft && (
                    <p className="semantic-review-speech-edit__disclosure">
                      Você aprova somente a redação. Os fatos continuam vindo da planta.
                    </p>
                  )}
                  <div className="semantic-review-speech-edit__footer">
                    <span className={`semantic-review-speech-edit__status semantic-review-speech-edit__status--${statusTone}`}>
                      {statusMessage}
                    </span>
                    <div className="semantic-review-speech-edit__actions">
                      <button
                        className="semantic-review-secondary-button"
                        type="button"
                        onClick={() => onCancelSpeechEdit(editKey)}
                        disabled={submitting}
                      >
                        {submitted ? 'Fechar' : 'Cancelar'}
                      </button>
                      {!submitted && (
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => onSubmitSpeechEdit(section, sectionIndex, speech, index)}
                          disabled={submitting || Boolean(validationMessage)}
                        >
                          {submitting
                            ? 'Enviando...'
                            : structuralReviewDraft
                              ? (speechTextUnchanged
                                ? 'Usar esta redação'
                                : 'Editar e enviar para reauditoria')
                              : (requiresHumanEdit ? 'Enviar edição' : 'Enviar sugestão')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="semantic-review-speech__meta">
              {showTechnicalMeta && speech.textSha256 && <small>{shortHash(speech.textSha256)}</small>}
              {showTechnicalMeta && speech.variantContextId && <small>{speech.variantContextId}</small>}
              {canEditSpeech && !editOpen && (
                <button
                  className="semantic-review-secondary-button semantic-review-speech-edit-toggle"
                  type="button"
                  onClick={() => onOpenSpeechEdit(editKey, speech)}
                  disabled={submitting}
                >
                  {structuralReviewDraft ? 'Usar ou editar redação' : (requiresHumanEdit ? 'Editar fala' : 'Sugerir ajuste')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function clientConfirmedProposalKey(section, proposal) {
  const sectionKey = String(section?.sectionKey || proposal?.sectionKey || '').trim();
  const proposalId = String(proposal?.proposalId || '').trim();
  return sectionKey && proposalId ? `${sectionKey}::${proposalId}` : '';
}

function activeClientConfirmationForProposal(confirmedSpeech, section, proposal) {
  const sectionKey = String(section?.sectionKey || proposal?.sectionKey || '').trim();
  return (Array.isArray(confirmedSpeech?.confirmations) ? confirmedSpeech.confirmations : [])
    .find(item => ['SELECTED', 'CONFIRMED'].includes(item?.status)
      && item?.sectionKey === sectionKey
      && item?.proposalId === proposal?.proposalId) || null;
}

function activeClientConfirmationsForSection(confirmedSpeech, section) {
  const sectionKey = String(section?.sectionKey || '').trim();
  return (Array.isArray(confirmedSpeech?.confirmations) ? confirmedSpeech.confirmations : [])
    .filter(item => ['SELECTED', 'CONFIRMED'].includes(item?.status) && item?.sectionKey === sectionKey);
}

function ConfirmableSpeechProposals({
  section,
  confirmedSpeech,
  drafts,
  disabled,
  confirmingProposalKey,
  applyingSectionKey,
  onDraftChange,
  onConfirm,
  onRevoke,
  onApply,
}) {
  const proposals = Array.isArray(section?.sectionSemanticOrientation?.confirmableSpeechProposals)
    ? section.sectionSemanticOrientation.confirmableSpeechProposals
    : [];
  if (proposals.length === 0) return null;
  const sectionKey = String(section?.sectionKey || '').trim();
  const activeConfirmations = activeClientConfirmationsForSection(confirmedSpeech, section);
  const minimum = Number(confirmedSpeech?.minSelectionCount || 3);
  const maximum = Number(confirmedSpeech?.maxSelectionCount || 8);
  const canApply = confirmedSpeech?.legacyReadOnly !== true
    && activeConfirmations.length >= minimum
    && activeConfirmations.length <= maximum;

  return (
    <div className="semantic-review-confirmable">
      <div className="semantic-review-confirmable__header">
        <div>
          <strong>Redações propostas para esta seção</strong>
          <p>
            Você aprova somente a redação. Os fatos continuam vindo da planta e serão revalidados pelo sistema.
          </p>
        </div>
        <span>{activeConfirmations.length} selecionada{activeConfirmations.length === 1 ? '' : 's'}</span>
      </div>
      <div className="semantic-review-confirmable__list">
        {confirmedSpeech?.legacyReadOnly === true && (
          <div className="alert alert-warning">
            Seleções do contrato factual antigo estão disponíveis apenas para histórico. Gere um novo candidate para selecionar redações sob o contrato atual.
          </div>
        )}
        {proposals.map((proposal, index) => {
          const key = clientConfirmedProposalKey(section, proposal);
          const persisted = activeClientConfirmationForProposal(confirmedSpeech, section, proposal);
          const draft = drafts[key] || {};
          const editing = draft.editing === true;
          const text = draft.text ?? proposal.text ?? '';
          const confirming = confirmingProposalKey === key;
          const changed = String(text).replace(/\s+/g, ' ').trim()
            !== String(proposal.text || '').replace(/\s+/g, ' ').trim();
          return (
            <div className={`semantic-review-confirmable__item${persisted ? ' is-confirmed' : ''}`} key={key || index}>
              <span className="semantic-review-confirmable__index">{index + 1}</span>
              <div className="semantic-review-confirmable__body">
                {editing ? (
                  <textarea
                    className="textarea semantic-review-confirmable__textarea"
                    value={text}
                    rows={3}
                    maxLength={220}
                    onChange={event => onDraftChange(key, { text: event.target.value })}
                    disabled={disabled || confirming || Boolean(persisted)}
                  />
                ) : (
                  <p>{persisted?.confirmedText || proposal.text}</p>
                )}
                {persisted ? (
                  <div className="semantic-review-confirmable__confirmed">
                    <div>
                      <strong>
                        {['CLIENT_EDITED', 'CLIENT_EDITED_FORM'].includes(persisted.confirmationMode)
                          ? 'Redação editada e selecionada'
                          : 'Redação selecionada sem alteração'}
                      </strong>
                      <small>
                        {persisted.actor?.role || 'cliente'} · {formatTimestamp(persisted.selectedAt || persisted.confirmedAt)} · {shortHash(persisted.confirmedTextSha256)}
                      </small>
                    </div>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => onRevoke(persisted)}
                      disabled={disabled || confirmedSpeech?.legacyReadOnly === true}
                    >
                      Descartar
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      className="semantic-review-confirmable__edit"
                      type="button"
                      onClick={() => onDraftChange(key, {
                        editing: !editing,
                        text: editing ? proposal.text : text,
                      })}
                      disabled={disabled || confirming}
                    >
                      {editing ? 'Restaurar redação original' : 'Editar redação'}
                    </button>
                    <button
                      className="btn btn-primary semantic-review-confirmable__confirm"
                      type="button"
                      onClick={() => onConfirm(section, proposal, { ...draft, text })}
                      disabled={disabled || confirming || !String(text).trim()}
                    >
                      {confirming
                        ? 'Selecionando...'
                        : changed
                        ? 'Editar e enviar para reauditoria'
                          : 'Usar esta redação'}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="semantic-review-confirmable__apply">
        <div>
          <strong>{activeConfirmations.length} de no mínimo {minimum} redações selecionadas</strong>
          <span>O sistema revalida os fatos pela planta. Aplicar cria candidate offline e não altera o M1.</span>
        </div>
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => onApply(section, activeConfirmations)}
          disabled={disabled || !canApply || applyingSectionKey === sectionKey}
        >
          {applyingSectionKey === sectionKey
            ? 'Criando candidate sucessor...'
            : 'Aplicar redações selecionadas'}
        </button>
      </div>
    </div>
  );
}

function ReviewSpeechAuditPanel({
  sections,
  loading,
  error,
  previewOnly = false,
  blockers = [],
  persistedSpeechEdits = null,
}) {
  const values = Array.isArray(sections) ? sections : [];
  const hasBlockers = Array.isArray(blockers) && blockers.length > 0;
  const title = previewOnly ? 'Falas candidatas' : 'Falas auditadas';
  const subtitle = previewOnly
    ? 'Textos candidatos do pack semântico em modo consulta. Esta área não abre revisão, não salva decisão e não publica o candidate offline.'
    : 'Conteúdo da revisão semântica em modo consulta. Esta área não salva decisão nem publica o candidate offline.';
  const loadingMessage = previewOnly ? 'Carregando falas candidatas...' : 'Carregando falas auditadas...';
  const emptyMessage = previewOnly
    ? 'Nenhuma fala candidata foi carregada para este candidate.'
    : 'Nenhuma fala auditada foi carregada para esta revisão.';

  return (
    <section className="semantic-review-panel semantic-review-speech-audit-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>{title}</h3>
          <p className="semantic-review-panel__subtitle">
            {subtitle}
          </p>
        </div>
        <StatusPill
          status={loading ? 'Carregando conteúdo' : countNoun(values.length, 'seção', 'seções')}
          tone={error ? 'warning' : 'muted'}
        />
      </div>

      {error && (
        <div className="alert alert-warning">
          {error}
        </div>
      )}

      {previewOnly && hasBlockers && (
        <div className="alert alert-warning semantic-review-speech-audit-alert">
          Candidate ainda em consulta: há pré-condições técnicas pendentes antes da revisão semântica.
        </div>
      )}

      {loading && values.length === 0 && (
        <p className="semantic-review-empty">{loadingMessage}</p>
      )}

      {!loading && values.length === 0 && !error && (
        <p className="semantic-review-empty">{emptyMessage}</p>
      )}

      {values.length > 0 && (
        <div className="semantic-review-speech-audit-list">
          {values.map((section, index) => {
            const sectionKey = reviewSectionDecisionKey(section, index);
            const sectionStatus = section.clientDecision || section.systemStatus || section.status || 'Sem decisão';
            return (
              <article className="semantic-review-speech-audit-section" key={sectionKey}>
                <div className="semantic-review-speech-audit-section__header">
                  <div>
                    <strong>{section.sectionName || 'Seção sem nome'}</strong>
                    <span>
                      {displayedSpeechesForSection(section).length} fala
                      {displayedSpeechesForSection(section).length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <StatusPill status={sectionStatus} />
                </div>
                <SectionOrientationBlock
                  orientation={section.sectionSemanticOrientation}
                  section={section}
                />
                <SectionSpeechList
                  section={section}
                  sectionIndex={index}
                  persistedSpeechEdits={persistedSpeechEdits}
                />
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SpeechAuditTechnicalPanel({ sections }) {
  const values = Array.isArray(sections) ? sections : [];
  if (values.length === 0) return null;

  return (
    <div className="semantic-review-panel">
      <div className="semantic-review-panel__header">
        <h3>Auditoria das Falas</h3>
        <StatusPill status={countNoun(values.length, 'seção', 'seções')} tone="muted" />
      </div>
      <div className="semantic-review-blockers semantic-review-blockers--compact">
        {values.map((section, sectionIndex) => {
          const speeches = displayedSpeechesForSection(section);
          const sectionStatus = section.clientDecision || section.systemStatus || section.status || 'Sem decisão';
          return (
            <div
              className="semantic-review-blocker"
              key={reviewSectionDecisionKey(section, sectionIndex)}
            >
              <strong>{section.sectionName || 'Seção sem nome'}</strong>
              <span>Status: {sectionStatus}</span>
              {section.canonicalSectionName && <span>Nome canônico: {section.canonicalSectionName}</span>}
              {section.sectionId && <span>Section ID: {section.sectionId}</span>}
              {section.sectionKey && <span>Section key: {section.sectionKey}</span>}
              {speeches.map((speech, speechIndex) => (
                <span key={speech.id || speech.textSha256 || speechIndex}>
                  Fala {speechIndex + 1}: {shortHash(speech.textSha256)}
                  {speech.variantContextId ? ` · ${speech.variantContextId}` : ''}
                </span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionDiagnosisPanel({
  diagnosis,
  loading,
  error,
  writing,
  onRefresh,
  onWrite,
}) {
  const totals = diagnosis?.totals || {};
  const evaluation = (
    diagnosis?.evaluation
    && typeof diagnosis.evaluation === 'object'
    && !Array.isArray(diagnosis.evaluation)
  )
    ? diagnosis.evaluation
    : null;
  const evaluationReady = evaluation?.evaluable === true;
  const evaluationBlocked = Boolean(diagnosis && !evaluationReady);
  const rawEvaluationStatus = typeof evaluation?.status === 'string'
    ? evaluation.status
    : '';
  const evaluationStatus = evaluationReady
    ? (rawEvaluationStatus || 'READY')
    : (
      evaluation?.evaluable === false && rawEvaluationStatus
        ? rawEvaluationStatus
        : 'EVALUATION_UNAVAILABLE'
    );
  const rows = sectionDiagnosisRows(diagnosis);
  const validEvaluationBlockers = (
    evaluation?.evaluable === false
    && Array.isArray(evaluation?.blockers)
  )
    ? evaluation.blockers.filter(blocker => (
      typeof blocker === 'string'
      || (
        blocker
        && typeof blocker === 'object'
        && !Array.isArray(blocker)
        && (
          typeof blocker.code === 'string'
          || typeof blocker.status === 'string'
        )
      )
    ))
    : [];
  const evaluationBlockers = validEvaluationBlockers.length > 0
    ? validEvaluationBlockers
    : (evaluationBlocked ? [evaluationStatus] : []);
  const observedPhysicalSections = Number.isFinite(Number(evaluation?.observedPhysicalSections))
    ? Number(evaluation.observedPhysicalSections)
    : 0;
  const evaluatedSections = Number.isFinite(Number(evaluation?.evaluatedSections))
    ? Number(evaluation.evaluatedSections)
    : 0;
  const status = loading
    ? 'Carregando'
    : diagnosis
      ? (
        evaluationBlocked
          ? sectionDiagnosisEvaluationLabel(evaluationStatus)
          : `${totals.high || 0} alto / ${totals.medium || 0} médio / ${totals.low || 0} baixo`
      )
      : 'Sem diagnóstico';
  const tone = error
    ? 'warning'
    : evaluationBlocked
      ? 'error'
      : toneForSectionDiagnosisTotals(totals);

  return (
    <section className="semantic-review-panel semantic-review-section-diagnosis-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Diagnóstico por Seção</h3>
          <p className="semantic-review-panel__subtitle">
            {evaluationBlocked
              ? 'A avaliação global atual não autoriza diagnóstico por seção. Veja o bloqueio global do candidate.'
              : 'Observado versus esperado no candidate atual.'}
          </p>
        </div>
        <StatusPill status={status} tone={tone} />
      </div>

      {error && (
        <div className="alert alert-warning">
          {error}
        </div>
      )}

      <div className="semantic-review-section-diagnosis-actions">
        <button
          className="semantic-review-secondary-button"
          type="button"
          onClick={onRefresh}
          disabled={loading || writing}
        >
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={onWrite}
          disabled={loading || writing || !diagnosis}
        >
          {writing ? 'Salvando...' : 'Salvar diagnóstico'}
        </button>
      </div>

      {loading && !diagnosis && (
        <p className="semantic-review-empty">Carregando diagnóstico por seção.</p>
      )}

      {!loading && !diagnosis && !error && (
        <p className="semantic-review-empty">Nenhum diagnóstico carregado para este candidate.</p>
      )}

      {diagnosis && evaluationBlocked && (
        <>
          <div className="semantic-review-activation-summary">
            <CompactMetric
              label="Seções físicas"
              value={observedPhysicalSections}
              tone="muted"
            />
            <CompactMetric
              label="Seções avaliadas"
              value={evaluatedSections}
              tone="warning"
            />
          </div>
          <div className="semantic-review-blockers semantic-review-blockers--compact">
            {evaluationBlockers.map((blocker, index) => {
              const candidateBlockerCode = typeof blocker === 'string'
                ? blocker
                : blocker?.code || blocker?.status || evaluationStatus;
              const blockerCode = typeof candidateBlockerCode === 'string'
                ? candidateBlockerCode
                : evaluationStatus;
              const candidateBlockerMessage = typeof blocker === 'object' && blocker
                ? blocker.message
                : null;
              const blockerMessage = typeof candidateBlockerMessage === 'string'
                ? candidateBlockerMessage
                : null;
              return (
                <div
                  className="semantic-review-blocker"
                  key={`${blockerCode || 'global-blocker'}-${index}`}
                >
                  <strong>{sectionDiagnosisEvaluationLabel(blockerCode)}</strong>
                  {blockerMessage && <span>{blockerMessage}</span>}
                </div>
              );
            })}
          </div>
        </>
      )}

      {diagnosis && evaluationReady && rows.length > 0 && (
        <div className="semantic-review-section-diagnosis-list">
          {rows.map((section, index) => {
            const primaryCode = section?.diagnosis?.primaryCode;
            const severity = section?.diagnosis?.severity || 'OK';
            const rowTone = toneForSectionDiagnosisSeverity(severity);
            return (
              <article
                className={`semantic-review-section-diagnosis-row semantic-review-section-diagnosis-row--${rowTone}`}
                key={section.sectionKey || section.sectionId || section.sectionName || index}
              >
                <div className="semantic-review-section-diagnosis-row__header">
                  <strong>{section.sectionName || 'Seção sem nome'}</strong>
                  {physicalSectionHint(section) && (
                    <span className="semantic-review-chip semantic-review-chip--muted">
                      {physicalSectionHint(section)}
                    </span>
                  )}
                  <StatusPill status={sectionDiagnosisLabel(primaryCode)} tone={rowTone} />
                </div>
                <div className="semantic-review-section-diagnosis-row__grid">
                  <span>Entendido como</span>
                  <strong>{section?.observed?.whatItIs || 'Não identificado'}</strong>
                  <span>Deveria ser</span>
                  <strong>{section?.expected?.whatItShouldBe || 'Sem evidência suficiente'}</strong>
                  <span>Ação</span>
                  <strong>{sectionDiagnosisActionLabel(section?.diagnosis?.action)}</strong>
                </div>
                {section?.diagnosis?.explanation && (
                  <p>{section.diagnosis.explanation}</p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FallbackResolutionList({
  items,
  onOpenMap,
  onRequestRegeneration,
  regeneratingSection,
}) {
  const values = Array.isArray(items) ? items : [];
  const overlapCount = values.filter(item => item?.overlapsClientReview === true).length;
  return (
    <section className="semantic-review-section-list semantic-review-resolution-list">
      <div className="semantic-review-section-list__header">
        <h3>Fala padrão operacional</h3>
        <span>{values.length}</span>
      </div>
      {values.length > 0 ? (
        <div className="semantic-review-resolution-items">
          {overlapCount > 0 && (
            <p className="semantic-review-empty">
              {overlapCount} {overlapCount === 1 ? 'área também aparece' : 'áreas também aparecem'} na revisão por identidade física; estes contadores não são aditivos.
            </p>
          )}
          {values.map((item, index) => {
            const reasonCodes = Array.isArray(item.reasonCodes) ? item.reasonCodes : [];
            const itemKey = reviewSectionDecisionKey(item, index);
            const isRegenerating = regeneratingSection === itemKey;
            return (
              <article className="semantic-review-resolution-item" key={itemKey}>
                <div className="semantic-review-resolution-item__main">
                   <strong>{item.sectionName}</strong>
                    {physicalSectionHint(item) && (
                      <span className="semantic-review-chip semantic-review-chip--muted">
                        {physicalSectionHint(item)}
                      </span>
                    )}
                   {item.overlapsClientReview === true && (
                     <span className="semantic-review-chip semantic-review-chip--warning">
                       {item.clientDecisionPending === true ? 'Também em revisão' : 'Também revisada'}
                     </span>
                   )}
                   {item.operationalFallbackScope === 'LEGACY_NAME_ONLY_HOMONYM_GROUP' && (
                     <span className="semantic-review-chip semantic-review-chip--muted">
                       Rótulo homônimo no runtime legado
                     </span>
                   )}
                  <p>{item.userMessage || 'Esta seção permanece em fala padrão operacional neste candidate.'}</p>
                  {item.operationalConstraintMessage && (
                    <p>{item.operationalConstraintMessage}</p>
                  )}
                  {reasonCodes.length > 0 && (
                    <div className="semantic-review-chip-list">
                      {reasonCodes.slice(0, 3).map(reason => (
                        <span className="semantic-review-chip semantic-review-chip--warning" key={reason}>
                          {labelForReason(reason)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="semantic-review-resolution-item__actions">
                  {item.canOpenMap && (
                    <button
                      type="button"
                      className="semantic-review-secondary-button"
                      onClick={() => onOpenMap(item)}
                    >
                      Alterar mapa
                    </button>
                  )}
                  {item.canRequestRegeneration && (
                    <button
                      type="button"
                      className="semantic-review-secondary-button"
                      disabled={isRegenerating}
                      onClick={() => onRequestRegeneration(item, index)}
                    >
                      {isRegenerating ? 'Solicitando...' : (item.actionLabel || 'Regenerar seção')}
                    </button>
                  )}
                  {!item.canOpenMap && !item.canRequestRegeneration && (
                    <span className="semantic-review-resolution-note">
                      {item.actionLabel || 'Manter fora do conjunto aceito'}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="semantic-review-empty">Nenhuma seção fora do conjunto aceito.</p>
      )}
    </section>
  );
}

function AutonomousReviewBlockedPanel({
  blocker,
  autonomousReview,
  previousCandidate,
}) {
  const summary = autonomousReview?.summary || {};
  const blockedSections = Array.isArray(autonomousReview?.blockedSections)
    ? autonomousReview.blockedSections
    : [];
  const visibleBlockedSections = blockedSections.slice(0, 10);
  const hiddenCount = Math.max(0, blockedSections.length - visibleBlockedSections.length);
  const isPlantStale = blocker?.code === 'CANDIDATE_PLANT_HASH_STALE' ||
    blocker?.status === 'CLIENT_REVIEW_EXPIRED_PLANT_CHANGED';
  const isAiProviderBlocked = blocker?.status === 'BLOCKED_AI_PROVIDER' ||
    String(blocker?.code || '').startsWith('AI_PROVIDER_');
  const isRuntimeSpeechArtifactBlocked = [
    'RUNTIME_SPEECH_ARTIFACTS_MISSING',
    'RUNTIME_SPEECH_PACK_EMPTY',
    'RUNTIME_SPEECH_AUDIT_EMPTY',
    'RUNTIME_SPEECH_PACK_STATUS_MISSING',
  ].includes(blocker?.code) || [
    'RUNTIME_SPEECH_ARTIFACTS_MISSING',
    'RUNTIME_SPEECH_PACK_EMPTY',
    'RUNTIME_SPEECH_AUDIT_EMPTY',
    'RUNTIME_SPEECH_PACK_STATUS_MISSING',
  ].includes(blocker?.status);
  const missingArtifacts = Array.isArray(blocker?.details?.missingArtifacts)
    ? blocker.details.missingArtifacts
    : [];
  const missingOptionalArtifacts = Array.isArray(blocker?.details?.missingOptionalArtifacts)
    ? blocker.details.missingOptionalArtifacts
    : [];
  const title = isPlantStale
    ? 'Candidate de planta anterior'
    : isAiProviderBlocked
      ? 'Geração de falas pausada'
      : isRuntimeSpeechArtifactBlocked
        ? 'Falas não geradas para revisão'
      : 'Candidate bloqueado antes da revisão';
  const subtitle = isPlantStale
    ? 'A planta mudou depois da geração deste candidate. Aguarde o processamento da planta atual antes de revisar ou aplicar.'
    : isAiProviderBlocked
      ? (blocker?.message || 'O provedor de IA não entregou falas reais para este candidate.')
      : isRuntimeSpeechArtifactBlocked
        ? (blocker?.message || 'O candidate foi criado, mas o pack de falas ainda não está pronto para a revisão semântica.')
      : 'O auditor autônomo não abriu revisão do cliente para este candidate.';

  return (
    <section className="semantic-review-panel semantic-review-autonomous-blocked">
      <div className="semantic-review-panel__header">
        <div>
          <h3>{title}</h3>
          <p className="semantic-review-panel__subtitle">
            {subtitle}
          </p>
        </div>
        <StatusPill status={blocker?.status || blocker?.code} tone={isAiProviderBlocked ? 'warning' : 'error'} />
      </div>

      {isPlantStale ? (
        <div className="semantic-review-blocked-summary">
          <DetailRow label="Hash do candidate" value={shortId(blocker?.details?.candidatePlantHash)} />
          <DetailRow label="Hash atual" value={shortId(blocker?.details?.currentPlantHash)} />
          <DetailRow
            label="Nova planta"
            value={blocker?.details?.newerPlantProcessing ? 'Processando' : 'Aguardando candidate'}
          />
        </div>
      ) : isAiProviderBlocked ? (
        <div className="semantic-review-blocked-summary">
          <DetailRow label="Motivo" value={labelForReason(blocker?.code)} />
          <DetailRow label="Fase" value="Runtime Speech Source" />
          <DetailRow
            label="Status"
            value={blocker?.details?.retryable ? 'Pode tentar novamente' : 'Aguardando provedor'}
          />
        </div>
      ) : isRuntimeSpeechArtifactBlocked ? (
        <>
          <div className="semantic-review-activation-summary">
            <CompactMetric
              label="Artefatos"
              value={missingArtifacts.length > 0 ? `${missingArtifacts.length} ausente${missingArtifacts.length === 1 ? '' : 's'}` : 'Incompleto'}
              tone="warning"
            />
            <CompactMetric
              label="Seções"
              value={blocker?.details?.totalSections ?? 0}
              tone="warning"
            />
            <CompactMetric
              label="Status"
              value={labelForStatus(blocker?.code || blocker?.status)}
              tone="error"
            />
          </div>

          <div className="semantic-review-blocked-summary">
            <DetailRow label="Fase" value={blocker?.phase || 'Runtime Speech Pack'} />
            <DetailRow label="Pack gerado" value={blocker?.details?.runtimeSpeechPackGenerated === true ? 'Sim' : 'Não'} />
            <DetailRow label="Modo" value={blocker?.details?.runtimeSpeechPackMode || 'Indisponível'} />
          </div>
        </>
      ) : (
        <>
          <div className="semantic-review-activation-summary">
            <CompactMetric
              label="Cobertura"
              value={formatPercent(summary.approvedCoverageRatio)}
              tone="warning"
            />
            <CompactMetric
              label="Mínimo"
              value={formatOptionalPercent(summary.minimumApprovedCoverageRatio)}
            />
            <CompactMetric
              label="Aprovadas"
              value={summary.approvedSections || 0}
              tone="success"
            />
          </div>

          <div className="semantic-review-blocked-summary">
            <DetailRow label="Seções totais" value={String(summary.totalSections || 0)} />
            <DetailRow label="Bloqueadas" value={String(summary.excludedSections || blockedSections.length || 0)} />
            <DetailRow label="Rejeitadas" value={String(summary.rejectedSections || 0)} />
            <DetailRow label="Revisão técnica" value={String(summary.requiresHumanReviewSections || 0)} />
          </div>
        </>
      )}

      {isRuntimeSpeechArtifactBlocked && (missingArtifacts.length > 0 || missingOptionalArtifacts.length > 0) && (
        <div className="semantic-review-blockers semantic-review-blockers--compact">
          {missingArtifacts.map(artifact => (
            <div className="semantic-review-blocker" key={`missing-${artifact}`}>
              <strong>Artefato obrigatório ausente</strong>
              <span>{artifact}</span>
            </div>
          ))}
          {missingOptionalArtifacts.map(artifact => (
            <div className="semantic-review-blocker" key={`missing-optional-${artifact}`}>
              <strong>Artefato complementar ausente</strong>
              <span>{artifact}</span>
            </div>
          ))}
        </div>
      )}

      {previousCandidate && (
        <div className="alert alert-warning semantic-review-previous-candidate">
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Candidate anterior com revisão</span>
            <CandidateIdBlock value={previousCandidate.candidateId} />
          </div>
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Estado anterior</span>
            <strong>{labelForStatus(previousCandidate.reviewStatus || previousCandidate.stagedRuntimeConfigStatus)}</strong>
          </div>
        </div>
      )}

      {visibleBlockedSections.length > 0 && (
        <div className="semantic-review-blocked-sections">
          <h4>Seções bloqueadas</h4>
          <div className="semantic-review-blocked-section-list">
            {visibleBlockedSections.map((section, index) => {
              const reasons = [
                ...(Array.isArray(section.blockReasons) ? section.blockReasons : []),
                ...(Array.isArray(section.reasonCodes) ? section.reasonCodes : []),
              ];
              const uniqueReasons = [...new Set(reasons)].slice(0, 4);
              return (
                <div className="semantic-review-blocked-section" key={reviewSectionDecisionKey(section, index)}>
                  <div>
                    <strong>{section.sectionName || 'Seção sem nome'}</strong>
                    <span>{labelForStatus(section.status)}</span>
                    {physicalSectionHint(section) && <span>{physicalSectionHint(section)}</span>}
                  </div>
                  <div className="semantic-review-chip-list">
                    {uniqueReasons.length > 0 ? uniqueReasons.map(reason => (
                      <span className="semantic-review-chip semantic-review-chip--warning" key={reason}>
                        {labelForReason(reason)}
                      </span>
                    )) : (
                      <span className="semantic-review-chip semantic-review-chip--muted">
                        Motivo indisponível
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {hiddenCount > 0 && (
            <p className="semantic-review-empty">
              Mais {hiddenCount} seção{hiddenCount === 1 ? '' : 'ões'} bloqueada{hiddenCount === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function ProcessingJobPanel({
  job,
  candidate,
  previousCandidate,
  legacyDiagnosticOnly = false,
  candidateArtifactHealth = null,
}) {
  if (!job?.exists) return null;

  const isProcessing = job.processing === true;
  const isFailed = job.failed === true;
  const isTerminal = semanticReviewProcessingJobTerminal(job);
  const isOutcomeReached = semanticReviewProcessingJobOutcomeReached(job);
  const isOfflineResearchRecorded = semanticReviewProcessingJobOfflineResearchRecorded(job);
  const isTerminalWithPendingOutcome = semanticReviewProcessingJobTerminalWithPendingOutcome(job);
  const isCandidatePrepared = job.candidatePrepared === true || job.hasCandidate === true;
  const isLegacyCandidatePrepared = legacyDiagnosticOnly
    && isCandidatePrepared
    && candidateArtifactHealth?.ok === true;
  const isReviewReady = semanticReviewProcessingJobReviewReady(job);
  const failure = job.failure && typeof job.failure === 'object' ? job.failure : {};
  const primaryErrorCode = failure.code || job.lastErrorCode || null;
  const primaryErrorLabel = primaryErrorCode ? labelForReason(primaryErrorCode) : '';
  const failureIssueCodes = Array.isArray(failure.issueCodes) ? failure.issueCodes : [];
  const failureSectionKeys = Array.isArray(failure.issues)
    ? [...new Set(failure.issues.map(issue => issue?.sectionKey).filter(Boolean))]
    : [];
  const isAiBlocked = job.status === 'BLOCKED_AI_PROVIDER' ||
    String(primaryErrorCode || '').startsWith('AI_PROVIDER_');
  const isDiagnosticOnly = job.diagnosticOnly === true && job.completedWithoutCandidate === true;
  const candidateDiffers = semanticReviewCandidateIsHistorical(job, candidate);
  let title = 'Reprocessamento encontrado';
  if (isAiBlocked) title = 'Geração pausada pela IA';
  else if (isDiagnosticOnly) title = 'Diagnóstico concluído';
  else if (isLegacyCandidatePrepared) title = 'Preparação legada concluída';
  else if (isProcessing) title = 'Analisando a planta';
  else if (isFailed) title = 'Não foi possível concluir';
  else if (isOfflineResearchRecorded) title = 'Pesquisa da planta concluída';
  else if (isOutcomeReached && isReviewReady) title = 'Novas falas para revisar';
  else if (isOutcomeReached) title = 'Processamento concluído';
  else if (isTerminalWithPendingOutcome) title = 'Processamento incompleto';
  else if (isTerminal) title = 'Processamento encerrado';

  let subtitle = 'A rodada ainda não abriu candidate ou revisão. A tela acompanha o job para não parecer vazia.';
  if (isAiBlocked) {
    subtitle = 'A planta foi salva, mas a geração das falas precisa do provedor de IA disponível.';
  } else if (isDiagnosticOnly) {
    subtitle = 'Esta rodada apenas conferiu o funcionamento. Nenhuma fala nova foi preparada.';
  } else if (isLegacyCandidatePrepared) {
    subtitle = 'Candidate e runtime-speech-pack preparados. Este diagnóstico legado não representa readiness M2 nem libera execução.';
  } else if (isProcessing) {
    subtitle = 'Estamos preparando as falas da planta. Você não precisa fazer nada agora.';
  } else if (isOfflineResearchRecorded) {
    subtitle = 'A planta foi processada para pesquisa. Nesta rodada não há falas para aprovar ou editar; o rádio continua com a programação atual.';
  } else if (candidateDiffers) {
    subtitle = 'As falas mostradas pertencem a uma rodada anterior e não representam o resultado desta planta.';
  } else if (isOutcomeReached && isReviewReady) {
    subtitle = 'Há falas novas, conferidas pelo sistema, para você aceitar, editar ou recusar.';
  } else if (isTerminalWithPendingOutcome && isCandidatePrepared) {
    subtitle = 'A estrutura foi preparada, mas ainda não há falas disponíveis para sua decisão.';
  } else if (isTerminalWithPendingOutcome) {
    subtitle = 'O processamento terminou sem preparar as falas esperadas. Veja a pendência antes de considerar a planta concluída.';
  } else if (isCandidatePrepared) {
    subtitle = 'A planta foi preparada, mas as falas ainda não estão prontas para sua decisão.';
  }

  let tone = 'muted';
  if (isAiBlocked || isFailed) tone = 'error';
  else if (isOutcomeReached && isReviewReady) tone = 'success';
  else if (isProcessing || isDiagnosticOnly || (isTerminalWithPendingOutcome && !isLegacyCandidatePrepared)) tone = 'warning';
  const candidateMetric = isCandidatePrepared
    ? 'Pronta'
    : job.completedWithoutCandidate
      ? 'Não criada'
      : 'Ainda não';
  const reviewMetric = isReviewReady
    ? 'Disponível'
    : isTerminal
      ? 'Não aberta'
      : 'Aguardando';

  return (
    <section className={`semantic-review-panel semantic-review-processing-panel semantic-review-processing-panel--${tone}`}>
      <div className="semantic-review-panel__header">
        <div>
          <h3>{title}</h3>
          <p className="semantic-review-panel__subtitle">{subtitle}</p>
        </div>
        <StatusPill status={job.status || job.jobStatus} tone={tone} />
      </div>

      <div className="semantic-review-activation-summary">
        <CompactMetric label="Planta analisada" value={labelForScope(job.regenerationScope)} tone={job.regenerationScope === 'SECTION' ? 'success' : 'warning'} />
        <CompactMetric
          label="Resultado"
          value={isLegacyCandidatePrepared ? 'Diagnóstico legado' : labelForDesiredOutcome(job.desiredOutcome)}
          tone={isLegacyCandidatePrepared ? 'muted' : (job.desiredOutcome === 'CLIENT_SEMANTIC_REVIEW' ? 'success' : 'muted')}
        />
        <CompactMetric label="Tentativa" value={`${job.attempt ?? 0} de ${job.maxAttempts ?? '-'}`} />
        <CompactMetric label="Preparação" value={candidateMetric} tone={isCandidatePrepared ? 'success' : 'warning'} />
        <CompactMetric
          label="Sua revisão"
          value={isLegacyCandidatePrepared ? 'Opcional' : reviewMetric}
          tone={isLegacyCandidatePrepared ? 'muted' : (isReviewReady ? 'success' : 'warning')}
        />
      </div>

      <details className="semantic-review-job-technical-details">
        <summary>Ver detalhes técnicos do processamento</summary>
        <div className="semantic-review-processing-grid">
          <DetailRow label="Job" value={shortId(job.jobId)} />
          <DetailRow label="Origem" value={labelForTrigger(job.trigger)} />
          <DetailRow label="Hash alvo" value={shortId(job.targetPlantHash)} />
          <DetailRow label="Hash atual" value={shortId(job.currentPlantHash)} />
          <DetailRow label="Planta atual" value={job.freshForCurrentPlant === true ? 'Sim' : (job.staleForCurrentPlant ? 'Não' : 'Indisponível')} />
          <DetailRow label="Atualizado em" value={job.updatedAt || job.completedAt || job.createdAt} />
        </div>
      </details>

      {primaryErrorCode && (
        <div className="semantic-review-blockers semantic-review-blockers--compact">
          <div className="semantic-review-blocker">
            <strong>{primaryErrorLabel}</strong>
            {primaryErrorLabel !== primaryErrorCode && <span>{primaryErrorCode}</span>}
            {failure.failurePhase && (
              <span>Fase: {labelForStatus(failure.failurePhase)}</span>
            )}
            {Number.isFinite(Number(failure.issueCount)) && (
              <span>Issues estruturais: {Number(failure.issueCount)}</span>
            )}
            {failureIssueCodes.length > 0 && (
              <span>Códigos: {failureIssueCodes.join(', ')}</span>
            )}
            {failureSectionKeys.length > 0 && (
              <span>Seções: {failureSectionKeys.join(', ')}</span>
            )}
            {failure.errorClass && <span>Classe: {failure.errorClass}</span>}
            {failure.errorFingerprint && <span>Fingerprint: {shortId(failure.errorFingerprint)}</span>}
            {Array.isArray(failure.attempts) && failure.attempts.length > 0 && (
              <span>Tentativas registradas: {failure.attempts.length}</span>
            )}
          </div>
        </div>
      )}

      {candidateDiffers && (
        <div className="alert alert-warning semantic-review-previous-candidate">
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Candidate histórico</span>
            <CandidateIdBlock value={candidate.candidateId} />
          </div>
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Regra operacional</span>
            <strong>Não é resultado deste job; auditoria, diagnóstico, falas e ações estão ocultos</strong>
          </div>
        </div>
      )}

      {!candidate?.exists && previousCandidate && (
        <div className="alert alert-warning semantic-review-previous-candidate">
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Candidate anterior com revisão</span>
            <CandidateIdBlock value={previousCandidate.candidateId} />
          </div>
          <div className="semantic-review-runtime-alert__item">
            <span className="semantic-review-runtime-alert__label">Estado anterior</span>
            <strong>{labelForStatus(previousCandidate.reviewStatus || previousCandidate.stagedRuntimeConfigStatus)}</strong>
          </div>
        </div>
      )}
    </section>
  );
}

function CandidatePendingPanel({
  candidate,
  candidatePhase,
  stateUi,
}) {
  if (!candidate?.exists) return null;

  return (
    <section className="semantic-review-panel semantic-review-candidate-pending">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Falas ainda não disponíveis</h3>
          <p className="semantic-review-panel__subtitle">
            A planta foi preparada, mas as falas ainda não estão prontas para sua decisão.
          </p>
        </div>
        <StatusPill status={candidatePhase || stateUi?.displayStatus || candidate.pipelineStatus || candidate.jobStatus} tone="warning" />
      </div>
      <div className="semantic-review-processing-grid">
        <DetailRow label="Candidate" value={<CandidateIdBlock value={candidate.candidateId} />} align="start" />
        <DetailRow label="Job" value={shortId(candidate.jobId)} />
        <DetailRow label="Ação sugerida" value={stateUi?.nextAction || 'Aguardar'} />
        <DetailRow label="Hash da planta" value={shortId(candidate.targetPlantHash)} />
      </div>
    </section>
  );
}

function RuntimeDiagnosticAdvicePanel({ advice }) {
  if (!advice?.available) return null;

  const reasonCodes = Array.isArray(advice.reasonCodes) ? advice.reasonCodes.slice(0, 8) : [];
  const missingData = Array.isArray(advice.missingData) ? advice.missingData.slice(0, 8) : [];
  const technical = advice.technical || {};
  const deterministicM1 = technical.scope === 'M1_FACTUAL_RUNTIME'
    || technical.deterministicRuntimeServed === true;
  const axisSignals = deterministicM1
    ? []
    : (Array.isArray(advice.axisSignals) ? advice.axisSignals.slice(0, 4) : []);
  const tone = advice.tone || 'muted';
  const schedulerBlocked = String(advice.status || '').startsWith('SCHEDULER_BLOCKED')
    || String(technical.textPath || '').startsWith('SCHEDULER_BLOCKED');
  const title = schedulerBlocked
    ? 'Rádio ao vivo — ciclo atual'
    : deterministicM1
      ? 'Última fala confirmada no rádio'
      : 'Diagnóstico auxiliar — caminho legado ou não factual';
  const subtitle = schedulerBlocked
    ? 'O rádio está aguardando o próximo ciclo. Isso não impede a revisão nem indica falha da planta.'
    : deterministicM1
      ? 'Esta é a última fala que o rádio entregou e confirmou. Ela não depende do candidate ou de uma revisão semântica.'
      : 'Este diagnóstico não representa a autoridade factual M1. Candidate, pack e eixos de estilo permanecem offline ou restritos a caminhos não factuais.';

  return (
    <section className={`semantic-review-panel semantic-review-diagnostic-panel semantic-review-diagnostic-panel--${tone}`}>
      <div className="semantic-review-panel__header">
        <div>
          <h3>{title}</h3>
          <p className="semantic-review-panel__subtitle">
            {subtitle}
          </p>
        </div>
        <StatusPill status={schedulerBlocked ? 'SCHEDULER_BLOCKED' : advice.status} tone={tone} />
      </div>

      <div className="semantic-review-activation-summary">
        <CompactMetric label="Responsável" value={labelForResponsible(advice.responsible)} tone={tone} />
        <CompactMetric label="Ação" value={advice.actionLabel || 'Ver detalhes'} tone={tone} />
        <CompactMetric label="Confiança" value={labelForConfidence(advice.confidence)} />
      </div>

      <div className="semantic-review-human-guidance">
        {advice.howToFix || 'Nenhuma ação do cliente é necessária neste momento.'}
      </div>

      <details className="semantic-review-debug-details semantic-review-debug-details--inline">
        <summary>Ver detalhes técnicos do diagnóstico</summary>
        {deterministicM1 && (
          <p className="semantic-review-technical-note">Diagnóstico factual M1. Candidate e assignment não substituem este registro.</p>
        )}
        <div className="semantic-review-processing-grid">
          <DetailRow label="Caminho" value={labelForReason(technical.textPath) || technical.textPath} />
          <DetailRow label="Seção" value={technical.sectionName || 'Indisponível'} />
          {!deterministicM1 && (
            <DetailRow label="Candidate offline" value={<CandidateIdBlock value={technical.candidateId} />} align="start" />
          )}
          {deterministicM1 && <DetailRow label="Degrau M1" value={technical.deterministicTier || 'Indisponível'} />}
        </div>

        {axisSignals.length > 0 && (
          <div className="semantic-review-diagnostic-axis-list">
            {axisSignals.map(axis => (
              <div className="semantic-review-diagnostic-axis" key={axis.axis}>
                <strong>{labelForAxis(axis.axis)}</strong>
                <span>Config.</span>
                <em>{formatDiagnosticSignal(axis.configured)}</em>
                <span>Efetivo</span>
                <em>{formatDiagnosticSignal(axis.effective)}</em>
                <span>Detectado</span>
                <em>{formatDiagnosticSignal(axis.detected)}</em>
              </div>
            ))}
          </div>
        )}

        {(reasonCodes.length > 0 || missingData.length > 0) && (
          <div className="semantic-review-chip-list semantic-review-diagnostic-chips">
            {reasonCodes.map(code => (
              <span className="semantic-review-chip semantic-review-chip--warning" key={`reason-${code}`}>
                {labelForReason(code)}
              </span>
            ))}
            {missingData.map(code => (
              <span className="semantic-review-chip semantic-review-chip--muted" key={`missing-${code}`}>
                falta {labelForReason(code)}
              </span>
            ))}
          </div>
        )}
      </details>
    </section>
  );
}

function LastDeterministicExecutionPanel({ execution }) {
  const observed = execution?.observed === true;
  const tone = execution?.playerEnded
    ? 'success'
    : observed
      ? 'warning'
      : 'muted';

  return (
    <section className="semantic-review-panel semantic-review-activation-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Última fala confirmada no rádio</h3>
          <p className="semantic-review-panel__subtitle">
            Esta é a última fala entregue e encerrada pelo rádio. O histórico da revisão não substitui esta confirmação.
          </p>
        </div>
        <StatusPill
          status={execution?.status || 'NO_DETERMINISTIC_EXECUTION_OBSERVED'}
          tone={tone}
        />
      </div>

      {observed ? (
        <>
          <div className="semantic-review-activation-summary">
            <CompactMetric label="Fala" value={execution.degrau || 'Indisponível'} tone={tone} />
            <CompactMetric label="Entrega" value={execution.deliveredToPlayer ? 'Entregue' : 'Não confirmado'} tone={tone} />
            <CompactMetric label="Término" value={execution.playerEnded ? 'Confirmado' : 'Pendente'} tone={tone} />
          </div>
          <details className="semantic-review-debug-details semantic-review-debug-details--inline">
            <summary>Ver detalhes técnicos da Última execução factual M1</summary>
            <div className="semantic-review-processing-grid">
              <DetailRow label="Seção" value={execution.sectionName || 'Indisponível'} />
              <DetailRow label="Servida em" value={formatTimestamp(execution.servedAt)} />
              <DetailRow label="Template" value={execution.templateId || 'Indisponível'} />
              <DetailRow label="Provider textual" value={execution.providerCalled ? 'Chamado' : 'Não chamado'} />
              <DetailRow label="Prova" value={shortId(execution.compositionProofSha256)} />
              <DetailRow label="Trace" value={shortId(execution.traceId)} />
            </div>
          </details>
        </>
      ) : (
        <div className="alert alert-warning">
          Nenhuma execução factual M1 foi observada no período disponível do diagnóstico. Isso não significa que o M1 esteja inativo; significa apenas que não há prova factual recente. Candidate e assignment permanecem offline e não provam execução.
        </div>
      )}
    </section>
  );
}

function LegacyDiagnosticOnlyPanel({ candidate, review }) {
  return (
    <section className="semantic-review-panel semantic-review-activation-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Histórico da loja</h3>
          <p className="semantic-review-panel__subtitle">
            Estas informações são antigas e servem apenas para consulta. Elas não alteram o que toca no rádio.
          </p>
        </div>
        <StatusPill status={LEGACY_DIAGNOSTIC_ONLY} tone="muted" />
      </div>
      <details className="semantic-review-debug-details semantic-review-debug-details--inline">
        <summary>Ver histórico técnico legado</summary>
        <h4 className="semantic-review-technical-heading">Histórico semântico legado</h4>
        <div className="semantic-review-processing-grid">
          <DetailRow label="Candidate histórico" value={<CandidateIdBlock value={candidate?.candidateId} />} align="start" />
          <DetailRow label="Job histórico" value={shortId(candidate?.jobId)} />
          <DetailRow label="Revisão histórica" value={labelForStatus(review?.reviewStatus || 'CLIENT_REVIEW_NOT_FOUND')} />
          <DetailRow label="Interação" value="Desativada" />
        </div>
      </details>
    </section>
  );
}

function OfflinePublicationPanel({
  request,
  publication,
  loading,
  onRefresh,
}) {
  const requestStatus = request?.status || null;
  const assignmentActive = publication?.assignmentActive === true;
  const failed = requestStatus === 'FAILED_FINAL';
  const retrying = requestStatus === 'FAILED_RETRYABLE';
  const running = requestStatus === 'RUNNING';
  const queued = requestStatus === 'QUEUED';
  const hasLegacyRequest = request?.exists === true;
  const status = assignmentActive
    ? 'Histórico legado: assignment registrado'
    : failed
      ? 'Histórico legado: publicação falhou'
      : running
        ? 'Histórico legado: request em execução no corte'
        : queued || retrying
          ? 'Histórico legado: request pendente no corte'
          : labelForStatus(publication?.status || 'OFFLINE_REVIEW_RECORDED');
  const tone = assignmentActive
    ? 'success'
    : failed
      ? 'error'
      : 'warning';

  return (
    <section className="semantic-review-panel semantic-review-activation-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Revisão semântica legada</h3>
          <p className="semantic-review-panel__subtitle">
            A decisão preserva o conjunto auditado e pode alimentar somente o corpus privado autorizado. Ela não publica candidate nem seleciona texto factual do M1.
          </p>
        </div>
        <div className="semantic-review-activation-panel__actions">
          <StatusPill status={status} tone={tone} />
        </div>
      </div>

      {assignmentActive ? (
        <div className="alert alert-info">
          Existe assignment anterior ao B2. Ele é histórico, não será reexecutado e não prova execução factual.
        </div>
      ) : failed ? (
        <div className="alert alert-error">
          Existe uma falha de publicação anterior ao B2. Ela permanece somente para auditoria; não há publicação a retentar.
        </div>
      ) : (
        <div className="semantic-review-activation-ready">
          <p className="semantic-review-empty">
            {hasLegacyRequest
              ? 'O request de ativação exibido é histórico e não será reconciliado nem executado.'
              : 'Revisão registrada. Nenhum staging, runtime config ou assignment será criado.'}
          </p>
        </div>
      )}

      <details className="semantic-review-debug-details semantic-review-debug-details--inline">
        <summary>Dados históricos de ativação</summary>
        <div className="semantic-review-section-diagnosis-actions">
          <button
            className="semantic-review-secondary-button"
            type="button"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? 'Atualizando...' : 'Atualizar dados'}
          </button>
        </div>
        <div className="semantic-review-technical-grid">
          <DetailRow label="Solicitação" value={requestStatus ? labelForStatus(requestStatus) : 'Ausente'} />
          <DetailRow label="Tentativa" value={request?.attempt ? `${request.attempt}/${request.maxAttempts || '?'}` : 'Aguardando'} />
          <DetailRow label="Candidate" value={<CandidateIdBlock value={publication?.candidateId} />} align="start" />
          <DetailRow label="Assignment legado" value={assignmentActive ? 'Histórico' : 'Ausente'} />
          <DetailRow label="Autoridade factual M1" value="Não" />
        </div>
        {request?.lastErrorCode && (
          <div className="semantic-review-blockers semantic-review-blockers--compact">
            <div className="semantic-review-blocker">
              <strong>{request.lastErrorCode}</strong>
              {request.lastError && <span>{request.lastError}</span>}
            </div>
          </div>
        )}
      </details>
    </section>
  );
}

function ReviewDecisionPanel({
  sections,
  decisions,
  speechDecisions,
  sectionSpeechDecisionModes,
  notes,
  submitting,
  detailLoading,
  detailError,
  speechEditStates,
  persistedSpeechEdits,
  confirmedSpeech,
  confirmedSpeechDrafts,
  confirmingProposalKey,
  applyingConfirmedSectionKey,
  onDecision,
  onBulkDecision,
  onAcceptAllSpeeches,
  onSpeechDecision,
  onToggleStyleExample,
  onNotesChange,
  onSubmit,
  onOpenSpeechEdit,
  onChangeSpeechEdit,
  onCancelSpeechEdit,
  onSubmitSpeechEdit,
  onMaterializeSpeechEdit,
  onConfirmedSpeechDraftChange,
  onConfirmSpeechProposal,
  onRevokeSpeechConfirmation,
  onApplyConfirmedSpeeches,
  materializingSpeechEditId,
}) {
  const reviewRows = sections.map((section, index) => {
    const speechState = sectionSpeechDecisionState(section, speechDecisions, sectionSpeechDecisionModes);
    const resolution = resolvedSectionDecision(
      section,
      index,
      decisions,
      speechDecisions,
      sectionSpeechDecisionModes
    );
    return { section, index, speechState, resolution };
  });
  const missingCount = reviewRows.filter(row => !row.resolution).length;
  const styleExampleCount = reviewRows.reduce((total, row) => total + row.speechState.styleExampleCount, 0);
  const advisorySectionCount = reviewRows.filter(row => sectionRequiresHumanEdit(row.section)).length;
  const canSubmit = sections.length > 0 && missingCount === 0 && !submitting && !detailLoading;

  return (
    <section className="semantic-review-panel semantic-review-decision-panel">
      <div className="semantic-review-panel__header">
        <div>
          <h3>Revisão do Cliente</h3>
          <p className="semantic-review-panel__subtitle">
            {advisorySectionCount > 0
              ? 'Há rascunhos estruturais cuja redação pode ser usada como está ou editada antes da aplicação. Os fatos continuam vindo da planta.'
              : 'Aceite todas as falas da seção de uma vez ou revise cada fala individualmente.'}
          </p>
        </div>
        <StatusPill
          status={detailLoading ? 'Carregando conteúdo' : (missingCount > 0 ? `${missingCount} pendente${missingCount === 1 ? '' : 's'}` : 'Pronto para salvar')}
          tone={detailLoading || missingCount > 0 ? 'warning' : 'success'}
        />
      </div>

      {detailError && (
        <div className="alert alert-warning">
          {detailError}
        </div>
      )}

      <div className="semantic-review-decision-toolbar">
        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => onBulkDecision(CLIENT_LEFT_FOR_FALLBACK_DECISION)}
          disabled={submitting || missingCount === 0}
          title="Manter seções pendentes fora do conjunto aceito"
        >
          Não aceitar pendentes
        </button>
        {(advisorySectionCount > 0 || styleExampleCount > 0) && (
          <span className="semantic-review-decision-toolbar__summary">
            {advisorySectionCount > 0
              ? `${countNoun(advisorySectionCount, 'seção', 'seções')} com rascunho para revisar`
              : `${styleExampleCount} exemplo${styleExampleCount === 1 ? '' : 's'} de estilo selecionado${styleExampleCount === 1 ? '' : 's'}`}
          </span>
        )}
      </div>

      <div className="semantic-review-decision-list">
        {reviewRows.map(({ section, index, speechState, resolution }) => {
          const sectionDecisionKey = reviewSectionDecisionKey(section, index);
          const selected = resolution || '';
          const selectedMeta = decisionMeta(selected);
          const requiresHumanEdit = sectionRequiresHumanEdit(section);
          const advisorySpeeches = advisorySpeechesForSection(section);
          const structuralAdvisorySection = requiresHumanEdit
            && advisorySpeeches.length > 0
            && advisorySpeeches.every(isStructuralReviewDraftSpeech);
          const canEditAdvisory = advisorySpeechIdentitiesComplete(section);
          const progressTone = requiresHumanEdit
            ? 'warning'
            : speechState.ready
            ? 'success'
            : (speechState.allDecided ? 'error' : 'warning');
          const allowedDecisions = new Set(
            Array.isArray(section.allowedClientDecisions)
              ? section.allowedClientDecisions
              : []
          );
          const resolutionDecisions = CLIENT_SECTION_RESOLUTION_DECISIONS.filter(decision =>
            allowedDecisions.size === 0 || allowedDecisions.has(decision.value)
          );
          return (
            <div className="semantic-review-decision-row" key={sectionDecisionKey}>
              <div className="semantic-review-decision-row__content">
                <div className="semantic-review-decision-row__section">
                  <strong>{section.sectionName}</strong>
                </div>
                <SectionOrientationBlock
                  orientation={section.sectionSemanticOrientation}
                  section={section}
                />
                <ConfirmableSpeechProposals
                  section={section}
                  confirmedSpeech={confirmedSpeech}
                  drafts={confirmedSpeechDrafts}
                  disabled={submitting || detailLoading}
                  confirmingProposalKey={confirmingProposalKey}
                  applyingSectionKey={applyingConfirmedSectionKey}
                  onDraftChange={onConfirmedSpeechDraftChange}
                  onConfirm={onConfirmSpeechProposal}
                  onRevoke={onRevokeSpeechConfirmation}
                  onApply={onApplyConfirmedSpeeches}
                />
                <div className={`semantic-review-speech-progress semantic-review-speech-progress--${progressTone}`}>
                  {requiresHumanEdit ? (
                    <div>
                      <strong>{structuralAdvisorySection
                        ? 'Use ou edite a redação antes de aplicar'
                        : 'Precisa ser editado'}</strong>
                      <span>{advisorySpeeches.length} rascunho{advisorySpeeches.length === 1 ? '' : 's'} · {structuralAdvisorySection
                        ? 'seleção de forma; fatos vindos da planta'
                        : 'aceite intacto bloqueado'}</span>
                    </div>
                  ) : (
                    <>
                      <div>
                        <strong>{speechState.acceptedCount} aceitas · mínimo {speechState.minimumAcceptedSpeechVariants}</strong>
                        <span>
                          {speechState.decidedCount} de {speechState.totalCount} revisadas
                          {speechState.styleExampleCount > 0
                            ? ` · ${speechState.styleExampleCount} de estilo`
                            : ''}
                        </span>
                      </div>
                      <button
                        className="btn btn-secondary semantic-review-accept-all-button"
                        type="button"
                        onClick={() => onAcceptAllSpeeches(section, index)}
                        disabled={submitting || detailLoading || !speechState.identitiesComplete}
                        title="Aceita o conjunto exato apresentado sem selecionar exemplos de estilo"
                      >
                        Aceitar todas desta seção
                      </button>
                    </>
                  )}
                </div>
                <SectionSpeechList
                  section={section}
                  sectionIndex={index}
                  editable={requiresHumanEdit ? canEditAdvisory : speechState.identitiesComplete}
                  showDecisionControls={!requiresHumanEdit}
                  submittingReview={submitting}
                  speechDecisions={speechDecisions}
                  editStates={speechEditStates}
                  persistedSpeechEdits={persistedSpeechEdits}
                  onSpeechDecision={onSpeechDecision}
                  onToggleStyleExample={onToggleStyleExample}
                  onOpenSpeechEdit={onOpenSpeechEdit}
                  onChangeSpeechEdit={onChangeSpeechEdit}
                  onCancelSpeechEdit={onCancelSpeechEdit}
                  onSubmitSpeechEdit={onSubmitSpeechEdit}
                  onMaterializeSpeechEdit={onMaterializeSpeechEdit}
                  materializingSpeechEditId={materializingSpeechEditId}
                />
                {!requiresHumanEdit && !speechState.identitiesComplete && (
                  <p className="semantic-review-resolution-note">
                    Decisão por fala bloqueada: faltam sectionKey, hash do conjunto apresentado ou hash de alguma fala. Use fallback, regeneração ou correção.
                  </p>
                )}
                {!requiresHumanEdit && speechState.identitiesComplete && !speechState.ready && (
                  <p className="semantic-review-resolution-note">
                    {speechState.allDecided && !speechState.variantContextsReady
                      ? 'As falas aceitas ainda não cobrem a variedade mínima exigida; regenere ou mantenha a seção fora do conjunto aceito.'
                      : speechState.allDecided
                      ? `Restaram ${speechState.acceptedCount} falas aceitas. A seção precisa de pelo menos ${speechState.minimumAcceptedSpeechVariants} e continuará fora do conjunto aceito até regenerar.`
                      : `Decida as ${speechState.pendingCount} fala${speechState.pendingCount === 1 ? '' : 's'} pendente${speechState.pendingCount === 1 ? '' : 's'}, ou use Aceitar todas desta seção.`}
                  </p>
                )}
                {!requiresHumanEdit && speechState.ready && (
                  <p className="semantic-review-resolution-note semantic-review-resolution-note--success">
                    Seção pronta com {speechState.acceptedCount} falas aceitas. Rejeições individuais permanecem fora do conjunto executável.
                  </p>
                )}
                {requiresHumanEdit && (
                  <p className="semantic-review-resolution-note">
                    {structuralAdvisorySection
                      ? 'Estes textos são rascunhos de apoio. Use a redação intacta ou edite; em ambos os casos os fatos são revalidados pela planta antes de gerar outro candidate.'
                      : 'Estes textos são rascunhos de apoio, não falas aprovadas. Envie uma edição para reauditoria ou encerre a seção com rejeição, não aceite, regeneração ou correção da planta.'}
                  </p>
                )}
              </div>
              <div className="semantic-review-decision-row__actions" role="group" aria-label={`Decisão para ${section.sectionName}`}>
                {resolutionDecisions.map(decision => (
                  <button
                    key={decision.value}
                    className={`semantic-review-decision-button semantic-review-decision-button--${decision.tone}${selected === decision.value ? ' is-selected' : ''}`}
                    type="button"
                    onClick={() => onDecision(section, index, decision.value)}
                    disabled={submitting}
                  >
                    {decision.label}
                  </button>
                ))}
              </div>
              <span className={`semantic-review-decision-row__status semantic-review-decision-row__status--${selectedMeta?.tone || 'muted'}`}>
                {selected === CLIENT_APPROVED_SECTION_DECISION
                  ? `Pronta · ${speechState.acceptedCount} aceitas`
                  : selectedMeta?.label || 'Sem decisão'}
              </span>
            </div>
          );
        })}
      </div>

      <label className="semantic-review-notes">
        <span>Observação da revisão</span>
        <textarea
          className="textarea"
          value={notes}
          onChange={event => onNotesChange(event.target.value)}
          placeholder="Opcional. Use para registrar contexto comercial da decisão."
          rows={3}
          disabled={submitting}
        />
      </label>

      <div className="semantic-review-decision-footer">
        <p className="semantic-review-empty">
          {missingCount > 0
            ? `Faltam ${countNoun(missingCount, 'decisão', 'decisões')} para fechar a revisão.`
            : styleExampleCount > 0
              ? `Ao registrar, ${styleExampleCount} fala${styleExampleCount === 1 ? '' : 's'} será${styleExampleCount === 1 ? '' : 'ão'} usada${styleExampleCount === 1 ? '' : 's'} explicitamente como exemplo privado de estilo. Nenhuma fala será publicada.`
              : 'Ao registrar, a decisão fica disponível para auditoria. Nenhuma fala ou candidate será publicado.'}
        </p>
        <button
          className="btn btn-primary"
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Registrando revisão...' : 'Registrar decisão semântica'}
        </button>
      </div>
    </section>
  );
}

export default function SemanticReviewScreen({ onNavigate = null }) {
  const { usuario } = useAppContext();
  const { confirm, ConfirmDialogPortal } = useConfirm();
  const clienteId = usuario?.id || null;
  const [status, setStatus] = useState(null);
  const [reviewDetail, setReviewDetail] = useState(null);
  const [reviewDetailLoading, setReviewDetailLoading] = useState(false);
  const [reviewDetailError, setReviewDetailError] = useState('');
  const [sectionDiagnosis, setSectionDiagnosis] = useState(null);
  const [sectionDiagnosisLoading, setSectionDiagnosisLoading] = useState(false);
  const [sectionDiagnosisError, setSectionDiagnosisError] = useState('');
  const [sectionDiagnosisWriting, setSectionDiagnosisWriting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [decisionDraft, setDecisionDraft] = useState({});
  const [speechDecisionDraft, setSpeechDecisionDraft] = useState({});
  const [sectionSpeechDecisionModes, setSectionSpeechDecisionModes] = useState({});
  const [clientNotes, setClientNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [speechEditStates, setSpeechEditStates] = useState({});
  const [materializingSpeechEditId, setMaterializingSpeechEditId] = useState('');
  const [confirmedSpeechDrafts, setConfirmedSpeechDrafts] = useState({});
  const [confirmingProposalKey, setConfirmingProposalKey] = useState('');
  const [applyingConfirmedSectionKey, setApplyingConfirmedSectionKey] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [openingHumanReview, setOpeningHumanReview] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState('');
  const [requestingClientReview, setRequestingClientReview] = useState(false);
  const statusRefreshInFlightRef = useRef(false);
  const trackedClientReviewJobIdRef = useRef(null);
  const lastProcessingJobRef = useRef({ key: '', active: false });
  const currentClienteIdRef = useRef(clienteId);
  const currentCandidateIdRef = useRef(null);
  const sectionDiagnosisRequestRef = useRef({
    seq: 0,
    clienteId: clienteId ? String(clienteId) : null,
    candidateId: null,
  });
  const sectionDiagnosisWriteRequestRef = useRef({
    seq: 0,
    clienteId: clienteId ? String(clienteId) : null,
    candidateId: null,
  });
  currentClienteIdRef.current = clienteId;

  const loadReviewDetail = useCallback(async (candidateId) => {
    if (!clienteId || !candidateId) {
      setReviewDetail(null);
      setReviewDetailError('');
      return null;
    }
    const requestedClienteId = String(clienteId);
    const requestedCandidateId = String(candidateId);
    const requestIsCurrent = () => (
      String(currentClienteIdRef.current || '') === requestedClienteId
      && String(currentCandidateIdRef.current || '') === requestedCandidateId
    );
    if (!requestIsCurrent()) return null;
    setReviewDetailLoading(true);
    setReviewDetailError('');
    setReviewDetail(null);
    try {
      const data = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidateId}`);
      if (!requestIsCurrent()) return null;
      const detail = data.review || null;
      setReviewDetail(detail);
      return detail;
    } catch (err) {
      if (!requestIsCurrent()) return null;
      setReviewDetail(null);
      setReviewDetailError(err.message || 'Não foi possível carregar as falas auditadas desta revisão.');
      return null;
    } finally {
      if (requestIsCurrent()) setReviewDetailLoading(false);
    }
  }, [clienteId]);

  const loadCandidateSpeechPreview = useCallback(async (candidateId) => {
    if (!clienteId || !candidateId) {
      setReviewDetail(null);
      setReviewDetailError('');
      return null;
    }
    const requestedClienteId = String(clienteId);
    const requestedCandidateId = String(candidateId);
    const requestIsCurrent = () => (
      String(currentClienteIdRef.current || '') === requestedClienteId
      && String(currentCandidateIdRef.current || '') === requestedCandidateId
    );
    if (!requestIsCurrent()) return null;
    setReviewDetailLoading(true);
    setReviewDetailError('');
    setReviewDetail(null);
    try {
      const data = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidateId}/candidate-preview`);
      if (!requestIsCurrent()) return null;
      const detail = data.preview || null;
      setReviewDetail(detail);
      return detail;
    } catch (err) {
      if (!requestIsCurrent()) return null;
      setReviewDetail(null);
      setReviewDetailError(err.message || 'Não foi possível carregar as falas candidatas deste candidate.');
      return null;
    } finally {
      if (requestIsCurrent()) setReviewDetailLoading(false);
    }
  }, [clienteId]);

  const loadSectionDiagnosis = useCallback(async (candidateId) => {
    const requestedClienteId = clienteId ? String(clienteId) : '';
    const requestedCandidateId = candidateId ? String(candidateId) : '';
    if (String(currentClienteIdRef.current || '') !== requestedClienteId) {
      return null;
    }
    if (!requestedClienteId || !requestedCandidateId) {
      const currentRequest = sectionDiagnosisRequestRef.current;
      sectionDiagnosisRequestRef.current = {
        seq: currentRequest.seq + 1,
        clienteId: requestedClienteId || null,
        candidateId: null,
      };
      const currentWriteRequest = sectionDiagnosisWriteRequestRef.current;
      sectionDiagnosisWriteRequestRef.current = {
        seq: currentWriteRequest.seq + 1,
        clienteId: requestedClienteId || null,
        candidateId: null,
      };
      setSectionDiagnosis(null);
      setSectionDiagnosisError('');
      setSectionDiagnosisLoading(false);
      setSectionDiagnosisWriting(false);
      return null;
    }
    if (String(currentCandidateIdRef.current || '') !== requestedCandidateId) {
      return null;
    }

    const request = {
      seq: sectionDiagnosisRequestRef.current.seq + 1,
      clienteId: requestedClienteId,
      candidateId: requestedCandidateId,
    };
    sectionDiagnosisRequestRef.current = request;
    const requestIsCurrent = () => {
      const currentRequest = sectionDiagnosisRequestRef.current;
      return (
        currentRequest.seq === request.seq
        && currentRequest.clienteId === request.clienteId
        && currentRequest.candidateId === request.candidateId
        && String(currentClienteIdRef.current || '') === request.clienteId
        && String(currentCandidateIdRef.current || '') === request.candidateId
      );
    };

    setSectionDiagnosisLoading(true);
    setSectionDiagnosisError('');
    setSectionDiagnosis(null);
    try {
      const data = await apiFetchJSON(
        `/api/semantic/client-review/${requestedClienteId}/${requestedCandidateId}/section-diagnosis`
      );
      if (!requestIsCurrent()) return null;
      const diagnosis = data?.diagnosis || null;
      const responseCandidateId = diagnosis?.candidateId ? String(diagnosis.candidateId) : '';
      const responseClienteId = diagnosis?.clienteId ? String(diagnosis.clienteId) : '';
      if (
        !diagnosis
        || responseCandidateId !== requestedCandidateId
        || responseClienteId !== requestedClienteId
      ) {
        setSectionDiagnosis(null);
        setSectionDiagnosisError('A resposta do diagnóstico não corresponde ao cliente e candidate solicitados.');
        return null;
      }
      setSectionDiagnosis(diagnosis);
      return diagnosis;
    } catch (err) {
      if (!requestIsCurrent()) return null;
      setSectionDiagnosis(null);
      setSectionDiagnosisError(err.message || 'Não foi possível carregar o diagnóstico por seção.');
      return null;
    } finally {
      if (requestIsCurrent()) setSectionDiagnosisLoading(false);
    }
  }, [clienteId]);

  const loadStatus = useCallback(async (options = {}) => {
    if (!clienteId) return;
    if (statusRefreshInFlightRef.current) return;
    const silent = options?.silent === true;
    statusRefreshInFlightRef.current = true;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const requestedJobId = String(
        options?.jobId || trackedClientReviewJobIdRef.current || ''
      ).trim();
      const statusQuery = requestedJobId
        ? `?jobId=${encodeURIComponent(requestedJobId)}`
        : '';
      const data = await apiFetchJSON(
        `/api/semantic/client-review/${clienteId}/latest-status${statusQuery}`
      );
      if (String(currentClienteIdRef.current || '') !== String(clienteId || '')) {
        return null;
      }
      const nextStatus = data.status || null;
      const nextCandidateId = nextStatus?.candidate?.candidateId || null;
      const nextLegacyDiagnosticOnly = semanticReviewIsLegacyDiagnosticOnly(nextStatus);
      const nextCandidateHistorical = semanticReviewCandidateIsHistorical(
        nextStatus?.processingJob,
        nextStatus?.candidate
      );
      const nextCandidateIdentity = nextCandidateId
        && !nextCandidateHistorical
        && !nextLegacyDiagnosticOnly
        ? String(nextCandidateId)
        : null;
      const previousCandidateIdentity = currentCandidateIdRef.current;
      currentCandidateIdRef.current = nextCandidateIdentity;
      const candidateArtifactsReady = !nextCandidateHistorical
        && !nextLegacyDiagnosticOnly
        && nextStatus?.candidateArtifactHealth?.ok === true;
      const shouldRefreshDetails = !silent || !semanticReviewStatusNeedsPolling(nextStatus);
      const currentDiagnosisRequest = sectionDiagnosisRequestRef.current;
      const candidateIdentityChanged = previousCandidateIdentity !== nextCandidateIdentity;
      const diagnosisIdentityChanged = Boolean(
        currentDiagnosisRequest.candidateId
        && (
          currentDiagnosisRequest.clienteId !== String(clienteId)
          || currentDiagnosisRequest.candidateId !== nextCandidateIdentity
        )
      );
      if (!candidateArtifactsReady || candidateIdentityChanged || diagnosisIdentityChanged) {
        const currentRequest = sectionDiagnosisRequestRef.current;
        sectionDiagnosisRequestRef.current = {
          seq: currentRequest.seq + 1,
          clienteId: clienteId ? String(clienteId) : null,
          candidateId: null,
        };
        const currentWriteRequest = sectionDiagnosisWriteRequestRef.current;
        sectionDiagnosisWriteRequestRef.current = {
          seq: currentWriteRequest.seq + 1,
          clienteId: clienteId ? String(clienteId) : null,
          candidateId: null,
        };
        setSectionDiagnosis(null);
        setSectionDiagnosisError('');
        setSectionDiagnosisLoading(false);
        setSectionDiagnosisWriting(false);
      }
      if (nextCandidateHistorical || nextLegacyDiagnosticOnly) {
        setReviewDetail(null);
        setReviewDetailError('');
        setReviewDetailLoading(false);
        setDecisionDraft({});
        setSpeechDecisionDraft({});
        setSectionSpeechDecisionModes({});
        setSpeechEditStates({});
        setConfirmedSpeechDrafts({});
        setConfirmingProposalKey('');
        setApplyingConfirmedSectionKey('');
        setMaterializingSpeechEditId('');
        setClientNotes('');
      }
      setStatus(nextStatus);
      if (shouldRefreshDetails) {
        if (nextCandidateHistorical || nextLegacyDiagnosticOnly) {
          // Candidate historico ou diagnostico-only nao carrega superficie interativa.
        } else if (nextCandidateId && nextStatus?.clientReview?.exists === true) {
          await loadReviewDetail(nextCandidateId);
        } else if (nextCandidateId && nextStatus?.candidate?.exists === true) {
          await loadCandidateSpeechPreview(nextCandidateId);
        } else {
          setReviewDetail(null);
          setReviewDetailError('');
        }
        if (
          candidateArtifactsReady
          && nextCandidateId
          && nextStatus?.candidate?.exists === true
        ) {
          await loadSectionDiagnosis(nextCandidateId);
        } else if (candidateArtifactsReady) {
          const currentRequest = sectionDiagnosisRequestRef.current;
          sectionDiagnosisRequestRef.current = {
            seq: currentRequest.seq + 1,
            clienteId: clienteId ? String(clienteId) : null,
            candidateId: null,
          };
          const currentWriteRequest = sectionDiagnosisWriteRequestRef.current;
          sectionDiagnosisWriteRequestRef.current = {
            seq: currentWriteRequest.seq + 1,
            clienteId: clienteId ? String(clienteId) : null,
            candidateId: null,
          };
          setSectionDiagnosis(null);
          setSectionDiagnosisError('');
          setSectionDiagnosisLoading(false);
          setSectionDiagnosisWriting(false);
        }
      }
      return nextStatus;
    } catch (err) {
      if (!silent) setError(err.message || 'Erro ao carregar revisão semântica');
      return null;
    } finally {
      statusRefreshInFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [clienteId, loadCandidateSpeechPreview, loadReviewDetail, loadSectionDiagnosis]);

  useEffect(() => {
    currentCandidateIdRef.current = null;
    trackedClientReviewJobIdRef.current = null;
    const currentRequest = sectionDiagnosisRequestRef.current;
    sectionDiagnosisRequestRef.current = {
      seq: currentRequest.seq + 1,
      clienteId: clienteId ? String(clienteId) : null,
      candidateId: null,
    };
    const currentWriteRequest = sectionDiagnosisWriteRequestRef.current;
    sectionDiagnosisWriteRequestRef.current = {
      seq: currentWriteRequest.seq + 1,
      clienteId: clienteId ? String(clienteId) : null,
      candidateId: null,
    };
    setSectionDiagnosis(null);
    setSectionDiagnosisError('');
    setSectionDiagnosisLoading(false);
    setSectionDiagnosisWriting(false);
  }, [clienteId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const shouldPollReviewStatus = semanticReviewStatusNeedsPolling(status);
  useEffect(() => {
    if (!clienteId || !shouldPollReviewStatus) return undefined;
    const interval = setInterval(() => {
      loadStatus({ silent: true });
    }, REVIEW_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [clienteId, loadStatus, shouldPollReviewStatus]);

  const review = status?.clientReview || {};
  const detailedSectionMap = useMemo(() => buildDetailedSectionMap(reviewDetail), [reviewDetail]);
  const speechEditProjectionMap = useMemo(() => buildSpeechEditProjectionMap(reviewDetail), [reviewDetail]);
  const fallbackResolutionItems = Array.isArray(review.fallbackResolutionItems)
    ? review.fallbackResolutionItems
    : [];
  const candidate = status?.candidate || {};
  useEffect(() => {
    setSpeechEditStates({});
    setMaterializingSpeechEditId('');
  }, [candidate.candidateId]);
  const candidateStatus = status?.candidateStatus || {};
  const currentRuntime = status?.currentRuntimeConfig || {};
  const stagedRuntimeConfig = status?.stagedRuntimeConfig || {};
  const stagedRuntime = stagedRuntimeConfig.runtimeConfig || {};
  const stagedProposalRuntime = stagedRuntimeConfig.proposedRuntimeConfig || {};
  const autonomousReview = status?.autonomousReview || {};
  const currentCandidateBlocker = status?.currentCandidateBlocker || null;
  const previousReviewableCandidate = status?.previousReviewableCandidate || null;
  const processingJob = status?.processingJob || {};
  const processingJobActive = semanticReviewProcessingJobInProgress(processingJob);
  const candidateHistoricalForProcessingJob = semanticReviewCandidateIsHistorical(
    processingJob,
    candidate
  );
  const processingJobRedundantWithCurrentReview = Boolean(
    processingJob.exists
    && !processingJobActive
    && processingJob.failed !== true
    && !candidateHistoricalForProcessingJob
    && review.exists === true
    && semanticReviewProcessingJobOutcomeReached(processingJob)
  );
  const processingJobOwnsDisplay = processingJob.exists
    && !processingJobRedundantWithCurrentReview;
  const showProcessingJobPanel = processingJob.exists
    && !processingJobRedundantWithCurrentReview;
  const runtimeTruth = status?.runtimeTruth || {};
  const runtimeActivationRequest = status?.runtimeActivationRequest || {};
  const offlinePublication = status?.offlinePublication || {};
  const lastDeterministicExecution = status?.lastDeterministicExecution || {};
  const runtimeDiagnosticAdvice = status?.runtimeDiagnosticAdvice || null;
  const candidateArtifactHealth = status?.candidateArtifactHealth || {};
  const stateProjection = status?.stateProjection || {};
  const stateLegacy = stateProjection.legacy || {};
  const stateCanonical = stateProjection.canonical || {};
  const stateReadiness = stateProjection.readiness || {};
  const stateUi = stateProjection.ui || {};
  const legacyDiagnosticOnly = semanticReviewIsLegacyDiagnosticOnly(status, review, reviewDetail);
  const canRequestClientReview = !legacyDiagnosticOnly || review.exists !== true;
  const plantFreshness = status?.plantFreshness || {};
  const candidateStaleForCurrentPlant = stateReadiness.staleForCurrentPlant === true ||
    plantFreshness.staleForCurrentPlant === true ||
    candidate.staleForCurrentPlant === true;
  const reviewBlockedByProcessingJob = Boolean(
    processingJob.exists && processingJobOwnsDisplay
  );
  const candidateMutationBlockedByJob = reviewBlockedByProcessingJob || legacyDiagnosticOnly;
  useEffect(() => {
    if (!legacyDiagnosticOnly) return;
    currentCandidateIdRef.current = null;
    setDecisionDraft({});
    setSpeechDecisionDraft({});
    setSectionSpeechDecisionModes({});
    setSpeechEditStates({});
    setConfirmedSpeechDrafts({});
    setConfirmingProposalKey('');
    setApplyingConfirmedSectionKey('');
    setMaterializingSpeechEditId('');
    setClientNotes('');
  }, [legacyDiagnosticOnly]);
  useEffect(() => {
    if (!processingJob.exists) return;
    const key = processingJobTrackingKey(processingJob);
    const previous = lastProcessingJobRef.current;
    if (key && previous.key === key && previous.active === true && processingJobActive === false) {
      const message = processingJobTerminalMessage(processingJob);
      if (message) setSubmitMessage(message);
    }
    lastProcessingJobRef.current = { key, active: processingJobActive };
  }, [processingJob, processingJobActive]);

  const visibleCandidateBlocker = reviewBlockedByProcessingJob ? null : currentCandidateBlocker;
  const activationConsent = review.activationConsent || {};
  const statusDisplay = processingJobOwnsDisplay
    ? (processingJob.status || processingJob.jobStatus || 'SEMANTIC_ONBOARDING_PROCESSING')
    : (
      stateUi.displayStatus
      || visibleCandidateBlocker?.status
      || visibleCandidateBlocker?.code
      || status?.status
      || review.reviewStatus
    );
  const statusTone = useMemo(() => toneForStatus(statusDisplay), [statusDisplay]);
  const editableSections = useMemo(() => {
    const sections = Array.isArray(review.sectionDecisions) ? review.sectionDecisions : [];
    if (review.reviewStatus !== CLIENT_REVIEW_WAITING_STATUS) return [];
    return sections
      .filter(section =>
        section.editableByClient === true &&
        (
          section.systemStatus === TECHNICALLY_APPROVED_SECTION
          || section.systemStatus === REVIEWABLE_ADVISORY_SECTION
        )
      )
      .map((section, index) => {
        const detail = detailedSectionMap.get(reviewSectionDecisionKey(section, index));
        return detail ? { ...section, ...detail } : section;
      });
  }, [review.reviewStatus, review.sectionDecisions, detailedSectionMap]);
  const candidatePhase = stateCanonical.transition
    || stateLegacy.nextAllowedPhase
    || candidateStatus.nextAllowedPhase
    || review.reviewStatus
    || candidate.pipelineStatus
    || candidate.jobStatus;
  const runtimeCandidateDiffers = Boolean(
    offlinePublication.assignedCandidateId &&
    candidate.candidateId &&
    offlinePublication.assignedCandidateId !== candidate.candidateId
  );
  const showAutonomousBlockedPanel = Boolean(visibleCandidateBlocker);
  const showRuntimeDiagnosticAdvice = runtimeDiagnosticAdvice?.available === true
    && (legacyDiagnosticOnly || !candidateHistoricalForProcessingJob);
  const showDecisionPanel = !legacyDiagnosticOnly
    && editableSections.length > 0
    && !candidateStaleForCurrentPlant
    && !reviewBlockedByProcessingJob;
  const reviewDetailSections = Array.isArray(reviewDetail?.sectionDecisions)
    ? reviewDetail.sectionDecisions
    : [];
  const candidateReviewDetailResolved = review.exists !== true || reviewDetail !== null;
  const reviewDetailPreviewOnly = reviewDetail?.previewOnly === true || review.exists !== true;
  const reviewDetailBlockers = Array.isArray(reviewDetail?.blockers) ? reviewDetail.blockers : [];
  const showSpeechAuditPanel = Boolean(
    !legacyDiagnosticOnly &&
    !reviewBlockedByProcessingJob &&
    !showDecisionPanel &&
    (review.exists === true || candidate.exists === true) &&
    (reviewDetailLoading || reviewDetailError || reviewDetail)
  );
  const autonomousReviewPassed = stateReadiness.autonomousReviewPassed === true;
  const autonomousReviewReadyForHumanReview = Boolean(
    candidate.candidateId &&
    !candidateStaleForCurrentPlant &&
    stateReadiness.blocked !== true &&
    !visibleCandidateBlocker &&
    autonomousReviewPassed
  );
  const canOpenHumanReview = !legacyDiagnosticOnly
    && autonomousReviewReadyForHumanReview
    && review.exists !== true
    && !reviewBlockedByProcessingJob;
  const showOfflinePublicationPanel = Boolean(
    !legacyDiagnosticOnly &&
    candidate.candidateId &&
    !candidateStaleForCurrentPlant &&
    !reviewBlockedByProcessingJob &&
    stateReadiness.blocked !== true &&
    !visibleCandidateBlocker &&
    (
      stateReadiness.clientReviewApproved === true ||
      SEMANTIC_APPROVAL_STATUSES.has(review.reviewStatus)
    )
  );
  const headerSubtitle = legacyDiagnosticOnly
    ? 'Veja o que o rádio realmente executou e o histórico da loja. Nenhuma ação de publicação está disponível aqui.'
    : candidateHistoricalForProcessingJob
    ? 'A planta está em uma nova rodada; o resultado anterior aparece apenas como histórico.'
    : showDecisionPanel
      ? 'Escolha o que fazer com as falas de cada seção: aceitar, editar ou recusar.'
      : reviewBlockedByProcessingJob
      ? 'A planta está sendo preparada. Quando houver algo para decidir, esta tela será atualizada.'
      : review.exists === true
        ? 'Veja as falas da loja, o que já aconteceu no rádio e o histórico de alterações.'
        : candidate.exists
          ? 'A planta foi reconhecida e está sendo preparada para gerar novas falas.'
          : 'Acompanhe o preparo das falas e o que já foi executado no rádio.';

  const requestClientReview = useCallback(async () => {
    if (!clienteId || (legacyDiagnosticOnly && review.exists === true)
      || requestingClientReview || processingJobActive) return;
    const confirmed = await confirm({
      title: 'Abrir nova revisão semântica?',
      message: 'A planta inteira será preparada para uma revisão do cliente. A fila poderá chamar o provedor textual e gerar custo. Nenhuma fala será publicada e o runtime M1 não será alterado.',
      confirmLabel: 'Abrir revisão',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    setRequestingClientReview(true);
    setError('');
    setSubmitMessage('');
    try {
      const result = await apiFetchJSON(
        `/api/semantic/client-review/${clienteId}/request-review`,
        {
          method: 'POST',
          body: JSON.stringify({
            scope: 'FULL',
            confirmFullAuthoring: true,
            uiVersion: 'semantic-review-ui/client-semantic-review-v1',
          }),
        }
      );
      const jobId = String(result?.request?.jobId || '').trim();
      if (!jobId) throw new Error('A fila não devolveu o job da solicitação');
      trackedClientReviewJobIdRef.current = jobId;
      setSubmitMessage(result.request.deduplicated
        ? `Já existia uma solicitação equivalente. Acompanhando o job ${jobId}.`
        : `Revisão semântica registrada. Acompanhando o job ${jobId}.`);
      await loadStatus({ jobId });
    } catch (err) {
      setError(err.message || 'Não foi possível abrir a revisão semântica');
    } finally {
      setRequestingClientReview(false);
    }
  }, [clienteId, confirm, legacyDiagnosticOnly, loadStatus, processingJobActive, requestingClientReview, review.exists]);

  const sectionDiagnosisMatchesCandidate = !sectionDiagnosis || (
    String(sectionDiagnosis.candidateId || '') === String(candidate.candidateId || '')
    && String(sectionDiagnosis.clienteId || '') === String(clienteId || '')
  );
  const sectionDiagnosisClean = sectionDiagnosisIsClean(sectionDiagnosis);
  const showSectionDiagnosisPanel = Boolean(
    !legacyDiagnosticOnly
    &&
    candidateArtifactHealth.ok === true
    && candidate.candidateId
    && !reviewBlockedByProcessingJob
    && sectionDiagnosisMatchesCandidate
    && (!sectionDiagnosisClean || Boolean(sectionDiagnosisError))
  );

  const writeSectionDiagnosis = useCallback(async () => {
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia diagnóstico ou alteração deste candidate.');
      return;
    }
    const requestedClienteId = clienteId ? String(clienteId) : '';
    const requestedCandidateId = candidate.candidateId ? String(candidate.candidateId) : '';
    if (
      !requestedClienteId
      || !requestedCandidateId
      || !sectionDiagnosis
      || String(currentClienteIdRef.current || '') !== requestedClienteId
      || String(currentCandidateIdRef.current || '') !== requestedCandidateId
      || String(sectionDiagnosis.candidateId || '') !== requestedCandidateId
      || String(sectionDiagnosis.clienteId || '') !== requestedClienteId
    ) {
      return;
    }

    const request = {
      seq: sectionDiagnosisRequestRef.current.seq + 1,
      clienteId: requestedClienteId,
      candidateId: requestedCandidateId,
    };
    sectionDiagnosisRequestRef.current = request;
    const writeRequest = {
      seq: sectionDiagnosisWriteRequestRef.current.seq + 1,
      clienteId: requestedClienteId,
      candidateId: requestedCandidateId,
    };
    sectionDiagnosisWriteRequestRef.current = writeRequest;
    const requestIsCurrent = () => {
      const currentRequest = sectionDiagnosisRequestRef.current;
      return (
        currentRequest.seq === request.seq
        && currentRequest.clienteId === request.clienteId
        && currentRequest.candidateId === request.candidateId
        && String(currentClienteIdRef.current || '') === request.clienteId
        && String(currentCandidateIdRef.current || '') === request.candidateId
      );
    };
    const writeIsLatest = () => {
      const currentWriteRequest = sectionDiagnosisWriteRequestRef.current;
      return (
        currentWriteRequest.seq === writeRequest.seq
        && currentWriteRequest.clienteId === writeRequest.clienteId
        && currentWriteRequest.candidateId === writeRequest.candidateId
      );
    };

    setSectionDiagnosisWriting(true);
    setSectionDiagnosisError('');
    setSubmitMessage('');
    try {
      const data = await apiFetchJSON(`/api/semantic/client-review/${requestedClienteId}/${requestedCandidateId}/section-diagnosis`, {
        method: 'POST',
        body: JSON.stringify({
          uiVersion: 'semantic-review-ui/v5',
        }),
      });
      if (!requestIsCurrent()) return;
      const diagnosis = data?.diagnosis || null;
      const responseCandidateId = diagnosis?.candidateId ? String(diagnosis.candidateId) : '';
      const responseClienteId = diagnosis?.clienteId ? String(diagnosis.clienteId) : '';
      if (
        !diagnosis
        || responseCandidateId !== requestedCandidateId
        || responseClienteId !== requestedClienteId
      ) {
        setSectionDiagnosis(null);
        setSectionDiagnosisError('A resposta salva não corresponde ao cliente e candidate solicitados.');
        return;
      }
      setSectionDiagnosis(diagnosis);
      const output = data?.outputPaths?.jsonPath ? `: ${shortId(data.outputPaths.jsonPath)}` : '.';
      setSubmitMessage(`Diagnóstico por seção salvo${output}`);
    } catch (err) {
      if (!requestIsCurrent()) return;
      setSectionDiagnosisError(err.message || 'Erro ao salvar diagnóstico por seção.');
    } finally {
      if (writeIsLatest()) setSectionDiagnosisWriting(false);
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, sectionDiagnosis]);

  useEffect(() => {
    setDecisionDraft({});
    setSpeechDecisionDraft({});
    setSectionSpeechDecisionModes({});
    setConfirmedSpeechDrafts({});
    setConfirmingProposalKey('');
    setApplyingConfirmedSectionKey('');
    setClientNotes('');
    setSubmitMessage('');
  }, [candidate.candidateId]);

  const updateConfirmedSpeechDraft = useCallback((key, patch) => {
    if (!key) return;
    setConfirmedSpeechDrafts(previous => ({
      ...previous,
      [key]: {
        ...(previous[key] || {}),
        ...patch,
      },
    }));
  }, []);

  const confirmSpeechProposal = useCallback(async (section, proposal, draft) => {
    if (!clienteId || !candidate.candidateId || candidateMutationBlockedByJob) return;
    const key = clientConfirmedProposalKey(section, proposal);
    if (!key) {
      setError('A proposta não possui identidade suficiente para selecionar a redação.');
      return;
    }
    setConfirmingProposalKey(key);
    setError('');
    setSubmitMessage('');
    try {
      await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/speech-confirmations`, {
        method: 'POST',
        body: JSON.stringify({
          sectionKey: proposal.sectionKey || section.sectionKey,
          sectionId: proposal.sectionId || section.sectionId,
          sectionName: proposal.sectionName || section.sectionName,
          proposalId: proposal.proposalId,
          proposalTextSha256: proposal.proposalTextSha256,
          confirmedText: draft.text || proposal.text,
        }),
      });
      setConfirmedSpeechDrafts(previous => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      setSubmitMessage('A redação foi selecionada. Os fatos serão revalidados pela planta antes do candidate sucessor.');
      await loadReviewDetail(candidate.candidateId);
    } catch (err) {
      setError(err.message || 'Não foi possível selecionar a redação desta seção.');
    } finally {
      setConfirmingProposalKey('');
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, loadReviewDetail]);

  const revokeSpeechConfirmation = useCallback(async confirmation => {
    if (!clienteId || !candidate.candidateId || !confirmation?.confirmationId
      || candidateMutationBlockedByJob) return;
    setError('');
    setSubmitMessage('');
    try {
      await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/speech-confirmations/${confirmation.confirmationId}`, {
        method: 'DELETE',
      });
      setSubmitMessage('A seleção foi descartada. Nenhum candidate foi publicado.');
      await loadReviewDetail(candidate.candidateId);
    } catch (err) {
      setError(err.message || 'Não foi possível descartar a seleção.');
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, loadReviewDetail]);

  const applyConfirmedSpeeches = useCallback(async (section, confirmations) => {
    if (!clienteId || !candidate.candidateId || candidateMutationBlockedByJob) return;
    const confirmationIds = (Array.isArray(confirmations) ? confirmations : [])
      .map(item => item?.confirmationId)
      .filter(Boolean);
    const approved = await confirm({
      title: 'Criar candidate sucessor?',
      message: `${confirmationIds.length} redações serão revalidadas contra a planta para ${section.sectionName}. O candidate atual e o M1 não serão alterados.`,
      confirmLabel: 'Criar candidate sucessor',
      cancelLabel: 'Cancelar',
    });
    if (!approved) return;
    const sectionKey = String(section?.sectionKey || '').trim();
    setApplyingConfirmedSectionKey(sectionKey);
    setError('');
    setSubmitMessage('');
    try {
      const result = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/speech-confirmations/apply`, {
        method: 'POST',
        body: JSON.stringify({ confirmationIds }),
      });
      setSubmitMessage(`Candidate sucessor solicitado para ${section.sectionName}. Job ${result.jobId || 'enfileirado'}; o M1 permanece inalterado.`);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Não foi possível criar o candidate sucessor com as redações selecionadas.');
    } finally {
      setApplyingConfirmedSectionKey('');
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, confirm, loadStatus]);

  const openHumanReview = useCallback(async () => {
    if (!clienteId || !candidate.candidateId) return;
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia a abertura de revisão deste candidate.');
      return;
    }

    setOpeningHumanReview(true);
    setError('');
    setSubmitMessage('');
    try {
      await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/open`, {
        method: 'POST',
        body: JSON.stringify({
          uiVersion: 'semantic-review-ui/v3',
        }),
      });
      setSubmitMessage('Revisão humana aberta.');
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Erro ao abrir revisão humana');
    } finally {
      setOpeningHumanReview(false);
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, loadStatus]);

  const setSectionDecision = useCallback((section, index, decision) => {
    if (candidateMutationBlockedByJob) return;
    const allowedClientDecisions = Array.isArray(section?.allowedClientDecisions)
      ? section.allowedClientDecisions
      : [];
    if (
      (allowedClientDecisions.length > 0 && !allowedClientDecisions.includes(decision))
      || (sectionRequiresHumanEdit(section) && decision === CLIENT_APPROVED_SECTION_DECISION)
    ) {
      setError(`A decisão selecionada não é permitida para ${section?.sectionName || 'esta seção'}.`);
      return;
    }
    const sectionKey = String(section?.sectionKey || '').trim();
    setDecisionDraft(previous => ({
      ...previous,
      [reviewSectionDecisionKey(section, index)]: decision,
    }));
    if (sectionKey) {
      setSpeechDecisionDraft(previous => removeSectionSpeechDecisions(previous, sectionKey));
      setSectionSpeechDecisionModes(previous => {
        const next = { ...previous };
        delete next[sectionKey];
        return next;
      });
    }
  }, [candidateMutationBlockedByJob]);

  const setSpeechDecision = useCallback((section, sectionIndex, speech, speechIndex, decision) => {
    if (candidateMutationBlockedByJob) return;
    const decisionKey = exactSpeechDecisionKey(section, speech);
    const sectionKey = String(section?.sectionKey || '').trim();
    if (!decisionKey || !sectionKey) {
      setError(`Não foi possível identificar a fala ${speechIndex + 1} de ${section?.sectionName || 'seção sem nome'}.`);
      return;
    }
    setError('');
    setSpeechDecisionDraft(previous => {
      const current = previous[decisionKey] || {};
      return {
        ...previous,
        [decisionKey]: {
          decision,
          useAsStyleExample: decision === CLIENT_ACCEPTED_SPEECH_DECISION
            ? current.useAsStyleExample === true
            : false,
        },
      };
    });
    setSectionSpeechDecisionModes(previous => ({
      ...previous,
      [sectionKey]: EXPLICIT_PER_SPEECH_DECISION_MODE,
    }));
    setDecisionDraft(previous => {
      const next = { ...previous };
      delete next[reviewSectionDecisionKey(section, sectionIndex)];
      return next;
    });
  }, [candidateMutationBlockedByJob]);

  const toggleSpeechStyleExample = useCallback((section, sectionIndex, speech, speechIndex, checked) => {
    if (candidateMutationBlockedByJob) return;
    const decisionKey = exactSpeechDecisionKey(section, speech);
    const sectionKey = String(section?.sectionKey || '').trim();
    if (!decisionKey || !sectionKey) {
      setError(`Não foi possível identificar a fala ${speechIndex + 1} de ${section?.sectionName || 'seção sem nome'}.`);
      return;
    }
    setSpeechDecisionDraft(previous => {
      const current = previous[decisionKey];
      if (current?.decision !== CLIENT_ACCEPTED_SPEECH_DECISION) return previous;
      return {
        ...previous,
        [decisionKey]: {
          ...current,
          useAsStyleExample: checked === true,
        },
      };
    });
    setSectionSpeechDecisionModes(previous => ({
      ...previous,
      [sectionKey]: EXPLICIT_PER_SPEECH_DECISION_MODE,
    }));
    setDecisionDraft(previous => {
      const next = { ...previous };
      delete next[reviewSectionDecisionKey(section, sectionIndex)];
      return next;
    });
  }, [candidateMutationBlockedByJob]);

  const acceptAllSectionSpeeches = useCallback((section, sectionIndex) => {
    if (candidateMutationBlockedByJob) return;
    if (sectionRequiresHumanEdit(section)) {
      setError(`Escolha uma redação de ${section?.sectionName || 'seção sem nome'} ou edite-a para reauditoria. A decisão da seção não substitui essa seleção.`);
      return;
    }
    const speechState = sectionSpeechDecisionState(section, speechDecisionDraft, sectionSpeechDecisionModes);
    if (!speechState.identitiesComplete) {
      setError(`Não é possível aceitar todas as falas de ${section?.sectionName || 'seção sem nome'} sem identidade e hashes completos.`);
      return;
    }
    const acceptedEntries = Object.fromEntries(speechState.speeches.map(speech => [
      exactSpeechDecisionKey(section, speech),
      {
        decision: CLIENT_ACCEPTED_SPEECH_DECISION,
        useAsStyleExample: false,
      },
    ]));
    setError('');
    setSpeechDecisionDraft(previous => ({
      ...removeSectionSpeechDecisions(previous, speechState.sectionKey),
      ...acceptedEntries,
    }));
    setSectionSpeechDecisionModes(previous => ({
      ...previous,
      [speechState.sectionKey]: CLIENT_ACCEPTED_ALL_PRESENTED_SPEECHES,
    }));
    setDecisionDraft(previous => {
      const next = { ...previous };
      delete next[reviewSectionDecisionKey(section, sectionIndex)];
      return next;
    });
  }, [candidateMutationBlockedByJob, sectionSpeechDecisionModes, speechDecisionDraft]);

  const setBulkDecision = useCallback(async (decision) => {
    if (candidateMutationBlockedByJob) return;
    const targetSections = decision === CLIENT_LEFT_FOR_FALLBACK_DECISION
      ? editableSections
        .map((section, index) => ({ section, index }))
        .filter(({ section, index }) => !resolvedSectionDecision(
          section,
          index,
          decisionDraft,
          speechDecisionDraft,
          sectionSpeechDecisionModes
        ))
      : editableSections.map((section, index) => ({ section, index }));

    if (decision === CLIENT_LEFT_FOR_FALLBACK_DECISION && targetSections.length > 0) {
      const sectionLabel = targetSections.length === 1 ? 'seção pendente' : 'seções pendentes';
      const confirmed = await confirm({
        title: `Marcar ${targetSections.length} ${sectionLabel}?`,
        message: 'Essas seções ficarão fora do conjunto aceito. O runtime factual M1 continuará escolhendo seu degrau determinístico de forma independente.',
        confirmLabel: 'Manter fora do conjunto',
        cancelLabel: 'Cancelar',
      });
      if (!confirmed) return;
    }
    if (targetSections.length === 0) return;
    setDecisionDraft(previous => ({
      ...previous,
      ...Object.fromEntries(
        targetSections.map(({ section, index }) => [reviewSectionDecisionKey(section, index), decision])
      ),
    }));
    const targetSectionKeys = new Set(
      targetSections.map(({ section }) => String(section?.sectionKey || '').trim()).filter(Boolean)
    );
    if (targetSectionKeys.size > 0) {
      setSpeechDecisionDraft(previous => {
        let next = previous;
        for (const sectionKey of targetSectionKeys) next = removeSectionSpeechDecisions(next, sectionKey);
        return next;
      });
      setSectionSpeechDecisionModes(previous => Object.fromEntries(
        Object.entries(previous).filter(([sectionKey]) => !targetSectionKeys.has(sectionKey))
      ));
    }
  }, [candidateMutationBlockedByJob, confirm, decisionDraft, editableSections, sectionSpeechDecisionModes, speechDecisionDraft]);

  const openSpeechEdit = useCallback((editKey, speech) => {
    if (candidateMutationBlockedByJob) return;
    setSpeechEditStates(previous => {
      const current = previous[editKey] || {};
      return {
        ...previous,
        [editKey]: {
          ...current,
          open: true,
          text: current.text ?? speech?.text ?? '',
          status: current.status === 'submitted' ? 'submitted' : 'editing',
          message: current.status === 'submitted' ? 'Aguardando reauditoria.' : '',
          editId: current.editId || null,
        },
      };
    });
  }, [candidateMutationBlockedByJob]);

  const changeSpeechEdit = useCallback((editKey, value) => {
    if (candidateMutationBlockedByJob) return;
    setSpeechEditStates(previous => ({
      ...previous,
      [editKey]: {
        ...(previous[editKey] || {}),
        open: true,
        text: value,
        status: 'editing',
        message: '',
        editId: null,
      },
    }));
  }, [candidateMutationBlockedByJob]);

  const cancelSpeechEdit = useCallback((editKey) => {
    setSpeechEditStates(previous => ({
      ...previous,
      [editKey]: {
        ...(previous[editKey] || {}),
        open: false,
        status: 'idle',
        message: '',
      },
    }));
  }, []);

  const submitSpeechEdit = useCallback(async (section, sectionIndex, speech, speechIndex) => {
    if (!clienteId || !candidate.candidateId || !speech?.textSha256) return;
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia a edição de fala deste candidate.');
      return;
    }
    const editKey = speechEditKey(section, sectionIndex, speech, speechIndex);
    const current = speechEditStates[editKey] || {};
    const editedText = normalizeSpeechEditText(current.text ?? '');
    const structuralReviewDraft = isStructuralReviewDraftSpeech(speech);
    const validationMessage = speechEditValidationMessage(editedText, speech.text, {
      allowUnchanged: structuralReviewDraft,
    });
    if (validationMessage) {
      setSpeechEditStates(previous => ({
        ...previous,
        [editKey]: {
          ...(previous[editKey] || {}),
          open: true,
          text: current.text ?? '',
          status: 'error',
          message: validationMessage,
        },
      }));
      return;
    }

    setSpeechEditStates(previous => ({
      ...previous,
      [editKey]: {
        ...(previous[editKey] || {}),
        open: true,
        text: current.text ?? editedText,
        status: 'submitting',
        message: 'Enviando sugestão...',
      },
    }));
    setError('');
    try {
      const result = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/speech-edit`, {
        method: 'POST',
        body: JSON.stringify({
          reviewer: {
            id: usuario?.id || clienteId,
            name: usuario?.nome || usuario?.nomeCurto || null,
          },
          uiVersion: 'semantic-review-ui/v4',
          sectionKey: section.sectionKey || null,
          sectionId: section.sectionId || null,
          sectionName: section.sectionName || null,
          targetSpeechTextSha256: speech.textSha256,
          editedText,
          clientNotes: clientNotes.trim() || null,
        }),
      });
      const finalStatus = result.editStatus || result.reaudit?.status || 'SUBMITTED_FOR_REAUDIT';
      const pendingReaudit = finalStatus === 'SUBMITTED_FOR_REAUDIT';
      const approvedByReaudit = finalStatus === 'APPROVED_BY_REAUDIT';
      const statusMessage = pendingReaudit
        ? 'Aguardando reauditoria.'
        : approvedByReaudit
          ? 'Aprovada na reauditoria.'
          : 'Rejeitada na reauditoria.';
      setSpeechEditStates(previous => ({
        ...previous,
        [editKey]: {
          ...(previous[editKey] || {}),
          open: pendingReaudit,
          text: editedText,
          status: pendingReaudit ? 'submitted' : 'resolved',
          message: statusMessage,
          editId: result.editId || null,
        },
      }));
      await loadReviewDetail(candidate.candidateId);
      setSubmitMessage(
        pendingReaudit
          ? `A frase de ${section.sectionName} aguarda reauditoria.`
          : `A frase de ${section.sectionName} foi ${approvedByReaudit ? 'aprovada' : 'rejeitada'} na reauditoria.`
      );
    } catch (err) {
      setSpeechEditStates(previous => ({
        ...previous,
        [editKey]: {
          ...(previous[editKey] || {}),
          open: true,
          text: current.text ?? editedText,
          status: 'error',
          message: err.message || 'Erro ao enviar sugestão.',
        },
      }));
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, clientNotes, loadReviewDetail, speechEditStates, usuario]);

  const materializeSpeechEdit = useCallback(async (speechEdit) => {
    const editId = String(speechEdit?.editId || '').trim();
    const parentCandidateId = String(candidate.candidateId || '').trim();
    if (!clienteId || !parentCandidateId || !editId) {
      setError('Não foi possível identificar a edição aprovada para gerar o candidate.');
      return;
    }
    if (speechEdit?.editStatus !== 'APPROVED_BY_REAUDIT') {
      setError('Somente uma edição aprovada na reauditoria pode gerar um novo candidate.');
      return;
    }
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia a geração de outro candidate.');
      return;
    }

    setMaterializingSpeechEditId(editId);
    setError('');
    setSubmitMessage('');
    try {
      const result = await apiFetchJSON(
        `/api/semantic/client-review/${clienteId}/${parentCandidateId}/speech-edits/${encodeURIComponent(editId)}/materialize`,
        { method: 'POST' }
      );
      setSubmitMessage(
        `Candidate sucessor enfileirado com a fala aprovada de ${result.sectionName || 'seção'}. Job ${result.jobId || 'em processamento'}.`
      );
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Não foi possível gerar o candidate com a fala aprovada.');
    } finally {
      setMaterializingSpeechEditId('');
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, loadStatus]);

  const submitReviewDecision = useCallback(async () => {
    if (!clienteId || !candidate.candidateId || editableSections.length === 0) return;
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia a decisão deste candidate.');
      return;
    }
    const reviewRows = editableSections.map((section, index) => {
      const speechState = sectionSpeechDecisionState(section, speechDecisionDraft, sectionSpeechDecisionModes);
      const decision = resolvedSectionDecision(
        section,
        index,
        decisionDraft,
        speechDecisionDraft,
        sectionSpeechDecisionModes
      );
      return { section, index, speechState, decision };
    });
    const missing = reviewRows.filter(row => !row.decision);
    if (missing.length > 0) {
      setError(`Faltam decisões para: ${missing.map(row => row.section.sectionName).join(', ')}`);
      return;
    }
    const invalidAdvisoryApprovals = reviewRows.filter(row =>
      row.decision === CLIENT_APPROVED_SECTION_DECISION && sectionRequiresHumanEdit(row.section)
    );
    if (invalidAdvisoryApprovals.length > 0) {
      setError(`Rascunhos não podem ser aceitos intactos em: ${invalidAdvisoryApprovals.map(row => row.section.sectionName).join(', ')}`);
      return;
    }
    const invalidApprovals = reviewRows.filter(row =>
      row.decision === CLIENT_APPROVED_SECTION_DECISION && !row.speechState.ready
    );
    if (invalidApprovals.length > 0) {
      setError(`Aprovação por fala incompleta em: ${invalidApprovals.map(row => row.section.sectionName).join(', ')}`);
      return;
    }

    setSubmittingReview(true);
    setError('');
    setSubmitMessage('');
    try {
      const decisions = reviewRows.map(({ section, speechState, decision }) => {
        const baseDecision = {
          sectionKey: section.sectionKey || null,
          sectionId: section.sectionId || null,
          sectionName: section.sectionName,
          sectionEvidenceHash: section.sectionEvidenceHash || null,
          decision,
          note: decisionMeta(decision)?.note || 'Decisão registrada pela tela de revisão semântica.',
        };

        if (decision !== CLIENT_APPROVED_SECTION_DECISION) {
          return {
            ...baseDecision,
            presentedSpeechSetSha256: section.presentedSpeechSetSha256 || null,
            speechDecisionMode: null,
            speechDecisions: [],
          };
        }

        const pureBatchAccept = speechState.mode === CLIENT_ACCEPTED_ALL_PRESENTED_SPEECHES &&
          speechState.acceptedCount === speechState.totalCount &&
          speechState.styleExampleCount === 0;
        const speechDecisionMode = pureBatchAccept
          ? CLIENT_ACCEPTED_ALL_PRESENTED_SPEECHES
          : EXPLICIT_PER_SPEECH_DECISION_MODE;
        const speechDecisions = pureBatchAccept
          ? []
          : speechState.speeches.map(speech => {
            const entry = speechDecisionEntry(speechDecisionDraft, section, speech);
            return {
              speechId: speech.speechId || speech.id || null,
              variantContextId: speech.variantContextId || null,
              textSha256: speech.textSha256,
              decision: entry.decision,
              useAsStyleExample: entry.decision === CLIENT_ACCEPTED_SPEECH_DECISION &&
                entry.useAsStyleExample === true,
            };
          });

        return {
          ...baseDecision,
          presentedSpeechSetSha256: speechState.presentedSpeechSetSha256,
          speechDecisionMode,
          speechDecisions,
        };
      });
      const result = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/decision`, {
        method: 'POST',
        body: JSON.stringify({
          reviewer: {
            id: usuario?.id || clienteId,
            name: usuario?.nome || usuario?.nomeCurto || null,
          },
          uiVersion: 'semantic-review-ui/post-m1-audit-v1',
          clientNotes: clientNotes.trim() || null,
          decisions,
        }),
      });
      setSubmitMessage(`Decisão semântica registrada: ${labelForStatus(result.reviewStatus)}. Nenhuma publicação foi iniciada.`);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Erro ao salvar revisão semântica');
    } finally {
      setSubmittingReview(false);
    }
  }, [
    candidate.candidateId,
    candidateMutationBlockedByJob,
    clienteId,
    clientNotes,
    decisionDraft,
    editableSections,
    loadStatus,
    sectionSpeechDecisionModes,
    speechDecisionDraft,
    usuario,
  ]);

  const openMapForFallbackSection = useCallback((section) => {
    if (!onNavigate) return;
    onNavigate('StoreMapControl', {
      source: 'semantic-review',
      focusSectionName: section?.sectionName || null,
      focusSectionRef: {
        sectionKey: section?.sectionKey || null,
        sectionId: section?.sectionId || null,
        sectionName: section?.sectionName || null,
      },
    });
  }, [onNavigate]);

  const requestFallbackRegeneration = useCallback(async (section, sectionIndex = 0) => {
    const sectionName = section?.sectionName;
    if (!clienteId || !candidate.candidateId || !sectionName) return;
    if (candidateMutationBlockedByJob) {
      setError('O job atual bloqueia a regeneração deste candidate.');
      return;
    }
    const regenerationSectionRefs = Array.isArray(section?.regenerationSectionRefs)
      && section.regenerationSectionRefs.length > 0
      ? section.regenerationSectionRefs
      : [{
          sectionKey: section?.sectionKey || null,
          sectionId: section?.sectionId || null,
          sectionName,
        }];
    const sectionReferenceKey = reviewSectionDecisionKey(section, sectionIndex);
    setRegeneratingSection(sectionReferenceKey);
    setError('');
    setActivationError('');
    setSubmitMessage('');
    try {
      const result = await apiFetchJSON(`/api/semantic/client-review/${clienteId}/${candidate.candidateId}/fallback-regeneration`, {
        method: 'POST',
        body: JSON.stringify({
          reviewer: {
            id: usuario?.id || clienteId,
            name: usuario?.nome || usuario?.nomeCurto || null,
          },
          uiVersion: 'semantic-review-ui/v3',
          sectionRefs: regenerationSectionRefs,
        }),
      });
      const enqueueStatus = result.enqueueResult?.status || 'registrada';
      const queueScope = result.enqueueResult?.regenerationScope || section?.regenerationScope || 'SECTION';
      setSubmitMessage(queueScope === 'FULL'
        ? `Reprocessamento completo solicitado para preservar as areas homonimas de ${sectionName}: ${enqueueStatus}.`
        : `Nova tentativa solicitada para ${sectionName}: ${enqueueStatus}.`);
      await loadStatus();
    } catch (err) {
      setError(err.message || 'Erro ao solicitar nova tentativa');
    } finally {
      setRegeneratingSection('');
    }
  }, [candidate.candidateId, candidateMutationBlockedByJob, clienteId, loadStatus, usuario]);

  if (!clienteId) {
    return (
      <main className="semantic-review-page">
        <h2>Falas da sua loja</h2>
        <div className="alert alert-warning">Cliente não identificado.</div>
      </main>
    );
  }

  return (
    <>
    <main className="semantic-review-page" data-lifecycle={SCREEN_LIFECYCLE}>
      <header className="semantic-review-header">
        <div>
          <h2>Falas da sua loja</h2>
          <p>{headerSubtitle}</p>
        </div>
        <div className="semantic-review-header__actions">
          {status && <StatusPill status={statusDisplay} tone={statusTone} />}
          {canRequestClientReview && (
            <button
              className="btn btn-primary"
              type="button"
              onClick={requestClientReview}
              disabled={loading || requestingClientReview || processingJobActive}
              title={legacyDiagnosticOnly
                ? 'Abre o diagnóstico legado; não autoriza M2 nem publica no rádio'
                : 'Abre uma revisão semântica para o cliente; não publica no rádio'}
            >
              {requestingClientReview
                ? 'Registrando...'
                : (legacyDiagnosticOnly ? 'Abrir diagnóstico legado' : 'Abrir revisão semântica')}
            </button>
          )}
          <button className="btn btn-secondary" type="button" onClick={loadStatus} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {submitMessage && <div className="alert alert-info">{submitMessage}</div>}

      {loading && !status && (
        <div className="semantic-review-panel">
          <p className="semantic-review-empty">Carregando status semântico...</p>
        </div>
      )}

      {!legacyDiagnosticOnly && !loading && status?.status === 'NO_SEMANTIC_CANDIDATE' && !processingJob.exists && (
        <div className="semantic-review-panel">
          <p className="semantic-review-empty">Nenhum candidate semântico encontrado para este cliente.</p>
        </div>
      )}

      {status && showProcessingJobPanel && (
        <ProcessingJobPanel
          job={processingJob}
          candidate={candidate}
          previousCandidate={previousReviewableCandidate}
          legacyDiagnosticOnly={legacyDiagnosticOnly}
          candidateArtifactHealth={candidateArtifactHealth}
        />
      )}

      {status && showRuntimeDiagnosticAdvice && (
        <RuntimeDiagnosticAdvicePanel advice={runtimeDiagnosticAdvice} />
      )}

      {status && (
        <LastDeterministicExecutionPanel execution={lastDeterministicExecution} />
      )}

      {status && legacyDiagnosticOnly && (
        <LegacyDiagnosticOnlyPanel candidate={candidate} review={review} />
      )}

      {status?.candidate?.exists
        && !candidateHistoricalForProcessingJob
        && !legacyDiagnosticOnly
        && candidateReviewDetailResolved && (
        <>
          <section className="semantic-review-metrics">
            <Metric
              label="Seções aprovadas na revisão"
              value={review.exists ? (review.summary?.approvedSections || 0) : (autonomousReview.summary?.approvedSections || 0)}
              tone="success"
            />
            <Metric
              label={showAutonomousBlockedPanel ? 'Bloqueadas' : 'Fala padrão operacional'}
              value={showAutonomousBlockedPanel
                ? (autonomousReview.summary?.excludedSections || listCount(autonomousReview.blockedSections))
                : (review.summary?.fallbackSections || 0)}
              tone="warning"
            />
            {(review.summary?.pendingClientSections || 0) > 0 && (
              <Metric label="Pendentes" value={review.summary.pendingClientSections} tone="warning" />
            )}
            {showAutonomousBlockedPanel && (
              <Metric label="Cobertura" value={formatPercent(autonomousReview.summary?.approvedCoverageRatio)} tone="muted" />
            )}
            {(review.summary?.requiresHumanReviewSections || 0) + (review.summary?.rejectedSections || 0) > 0 && (
              <Metric
                label="Atenção"
                value={(review.summary?.requiresHumanReviewSections || 0) + (review.summary?.rejectedSections || 0)}
                tone="muted"
              />
            )}
          </section>

          {runtimeCandidateDiffers && (
            <div className="alert alert-warning semantic-review-runtime-alert">
              <div className="semantic-review-runtime-alert__item">
                <span className="semantic-review-runtime-alert__label">Candidate atribuído (offline)</span>
                <CandidateIdBlock value={offlinePublication.assignedCandidateId} />
              </div>
              <div className="semantic-review-runtime-alert__item">
                <span className="semantic-review-runtime-alert__label">Proposta em revisão</span>
                <CandidateIdBlock value={candidate.candidateId} />
              </div>
            </div>
          )}

          {showAutonomousBlockedPanel && (
            <AutonomousReviewBlockedPanel
              blocker={visibleCandidateBlocker}
              autonomousReview={autonomousReview}
              previousCandidate={previousReviewableCandidate}
            />
          )}

          {canOpenHumanReview && (
            <section className="semantic-review-panel">
              <div className="semantic-review-panel__header">
                <div>
                  <h3>Revisão Humana</h3>
                  <p className="semantic-review-panel__subtitle">
                    O auditor técnico aprovou o candidate. Abra a revisão humana para registrar a decisão semântica.
                  </p>
                </div>
                <StatusPill status="CLIENT_ACTIVATION_CLIENT_REVIEW_REQUIRED" tone="warning" />
              </div>
              <button className="btn btn-primary" type="button" onClick={openHumanReview} disabled={openingHumanReview}>
                {openingHumanReview ? 'Abrindo...' : 'Abrir revisão humana'}
              </button>
            </section>
          )}

          {showSectionDiagnosisPanel && (
            <SectionDiagnosisPanel
              diagnosis={sectionDiagnosis}
              loading={sectionDiagnosisLoading}
              error={sectionDiagnosisError}
              writing={sectionDiagnosisWriting}
              onRefresh={() => loadSectionDiagnosis(candidate.candidateId)}
              onWrite={writeSectionDiagnosis}
            />
          )}

          {showDecisionPanel && (
            <ReviewDecisionPanel
              sections={editableSections}
              decisions={decisionDraft}
              speechDecisions={speechDecisionDraft}
              sectionSpeechDecisionModes={sectionSpeechDecisionModes}
              notes={clientNotes}
              submitting={submittingReview}
              detailLoading={reviewDetailLoading}
              detailError={reviewDetailError}
              speechEditStates={speechEditStates}
              persistedSpeechEdits={speechEditProjectionMap}
              confirmedSpeech={reviewDetail?.confirmedSpeech || null}
              confirmedSpeechDrafts={confirmedSpeechDrafts}
              confirmingProposalKey={confirmingProposalKey}
              applyingConfirmedSectionKey={applyingConfirmedSectionKey}
              onDecision={setSectionDecision}
              onBulkDecision={setBulkDecision}
              onAcceptAllSpeeches={acceptAllSectionSpeeches}
              onSpeechDecision={setSpeechDecision}
              onToggleStyleExample={toggleSpeechStyleExample}
              onNotesChange={setClientNotes}
              onSubmit={submitReviewDecision}
              onOpenSpeechEdit={openSpeechEdit}
              onChangeSpeechEdit={changeSpeechEdit}
              onCancelSpeechEdit={cancelSpeechEdit}
              onSubmitSpeechEdit={submitSpeechEdit}
              onMaterializeSpeechEdit={materializeSpeechEdit}
              onConfirmedSpeechDraftChange={updateConfirmedSpeechDraft}
              onConfirmSpeechProposal={confirmSpeechProposal}
              onRevokeSpeechConfirmation={revokeSpeechConfirmation}
              onApplyConfirmedSpeeches={applyConfirmedSpeeches}
              materializingSpeechEditId={materializingSpeechEditId}
            />
          )}

          {showSpeechAuditPanel && (
            <ReviewSpeechAuditPanel
              sections={reviewDetailSections}
              loading={reviewDetailLoading}
              error={reviewDetailError}
              previewOnly={reviewDetailPreviewOnly}
              blockers={reviewDetailBlockers}
              persistedSpeechEdits={speechEditProjectionMap}
            />
          )}

          {showOfflinePublicationPanel && (
            <OfflinePublicationPanel
              request={runtimeActivationRequest}
              publication={offlinePublication}
              loading={loading}
              onRefresh={loadStatus}
            />
          )}

          {!showAutonomousBlockedPanel &&
            !canOpenHumanReview &&
            !showDecisionPanel &&
            !showOfflinePublicationPanel &&
            !reviewBlockedByProcessingJob &&
            review.exists !== true && (
              <CandidatePendingPanel
                candidate={candidate}
                candidatePhase={candidatePhase}
                stateUi={stateUi}
              />
            )}

          <details className="semantic-review-technical-details">
            <summary>Detalhes técnicos</summary>
            <section className="semantic-review-grid">
              <div className="semantic-review-panel">
                <div className="semantic-review-panel__header">
                  <h3>Candidate</h3>
                  <StatusPill status={candidatePhase} />
                </div>
                <DetailRow label="Candidate" value={<CandidateIdBlock value={candidate.candidateId} />} align="start" />
                <DetailRow label="Job" value={shortId(candidate.jobId)} />
                <DetailRow label="Estado canônico" value={stateCanonical.transition} />
                <DetailRow label="Ação sugerida" value={stateUi.nextAction} />
                <DetailRow label="Hash da planta" value={shortId(candidate.targetPlantHash || candidateStatus.plantSnapshotPlantHash)} />
                <DetailRow label="Hash atual" value={shortId(candidate.currentPlantHash || plantFreshness.currentPlantHash)} />
                <DetailRow
                  label="Planta atual"
                  value={candidateStaleForCurrentPlant ? 'Não' : (candidate.freshForCurrentPlant === true ? 'Sim' : 'Indisponível')}
                />
              </div>

              <div className="semantic-review-panel">
                <div className="semantic-review-panel__header">
                  <h3>Revisão e histórico legado</h3>
                  <StatusPill status={review.reviewStatus} />
                </div>
                <DetailRow
                  label="Revisão semântica"
                  value={activationConsent.semanticApproval
                    ? 'Aprovado'
                    : (autonomousReviewReadyForHumanReview ? 'Auditor aprovado; revisão humana pendente' : 'Não aprovado')}
                />
                <DetailRow label="Disposição" value={labelForStatus(offlinePublication.status || 'OFFLINE_REVIEW_RECORDED')} />
                <DetailRow label="Assignment legado" value={offlinePublication.assignmentActive ? 'Histórico' : 'Ausente'} />
                <DetailRow label="Autoridade factual M1" value="Não" />
              </div>

              <div className="semantic-review-panel">
                <div className="semantic-review-panel__header">
                  <h3>Execução factual M1</h3>
                  <StatusPill status={lastDeterministicExecution.status || 'NO_DETERMINISTIC_EXECUTION_OBSERVED'} />
                </div>
                <DetailRow label="Degrau" value={lastDeterministicExecution.degrau || 'Indisponível'} />
                <DetailRow label="Seção" value={lastDeterministicExecution.sectionName || 'Indisponível'} />
                <DetailRow label="Entregue ao player" value={lastDeterministicExecution.deliveredToPlayer ? 'Sim' : 'Não confirmado'} />
                <DetailRow label="Término confirmado" value={lastDeterministicExecution.playerEnded ? 'Sim' : 'Não'} />
                <DetailRow label="Trace" value={shortId(lastDeterministicExecution.traceId)} />
              </div>

              <div className="semantic-review-panel">
                <div className="semantic-review-panel__header">
                  <h3>Histórico legado/offline</h3>
                  <StatusPill status="Não factual" tone="muted" />
                </div>
                <DetailRow label="Candidate atribuído" value={<CandidateIdBlock value={currentRuntime.candidateId} />} align="start" />
                <DetailRow label="Tráfego legado" value={`${currentRuntime.trafficPercent || 0}%`} />
                <DetailRow label="Somente log" value={currentRuntime.logOnly ? 'Sim' : 'Não'} />
                <DetailRow label="Fallback legado" value={currentRuntime.fallbackEnabled ? 'Ligado' : 'Desligado'} />
                <DetailRow label="Assignment canônico" value={runtimeTruth.canonicalAssignmentActive ? 'Atribuído' : 'Não atribuído'} />
                <DetailRow label="Candidate proposto" value={<CandidateIdBlock value={stagedRuntime.candidateId} />} align="start" />
                <DetailRow label="Tráfego proposto" value={`${stagedProposalRuntime.trafficPercent || status?.stagedRuntimeConfig?.targetTrafficPercent || 0}%`} />
                <DetailRow label="Fora da publicação" value={listCount(stagedProposalRuntime.excludedSectionNames || stagedRuntime.excludedSectionNames)} />
              </div>

              <SpeechAuditTechnicalPanel sections={reviewDetailSections} />
            </section>

            {sectionDiagnosisClean && (
              <SectionDiagnosisPanel
                diagnosis={sectionDiagnosis}
                loading={sectionDiagnosisLoading}
                error={sectionDiagnosisError}
                writing={sectionDiagnosisWriting}
                onRefresh={() => loadSectionDiagnosis(candidate.candidateId)}
                onWrite={writeSectionDiagnosis}
              />
            )}
          </details>

          {(fallbackResolutionItems.length > 0
            || (!showDecisionPanel && listCount(review.pendingClientSectionNames) > 0)
            || listCount(review.requiresHumanReviewSectionNames) > 0) && (
            <section className="semantic-review-sections">
              {fallbackResolutionItems.length > 0 && (
                <FallbackResolutionList
                  items={fallbackResolutionItems}
                  onOpenMap={openMapForFallbackSection}
                  onRequestRegeneration={requestFallbackRegeneration}
                  regeneratingSection={regeneratingSection}
                />
              )}
            {!showDecisionPanel && listCount(review.pendingClientSectionNames) > 0 && (
              <SectionList title="Pendentes" items={review.pendingClientSectionNames} tone="default" />
            )}
            {listCount(review.requiresHumanReviewSectionNames) > 0 && (
                <SectionList title="Revisão técnica" items={review.requiresHumanReviewSectionNames} tone="muted" />
              )}
            </section>
          )}
        </>
      )}
    </main>
    <ConfirmDialogPortal />
    </>
  );
}
